import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchOfficials, fetchDossierStatus,
  adminVerifyKey, adminFetchStats, adminFetchTasks, adminBuildDossier, adminBuildBatch, adminBuildMissingDossiers,
  adminDeleteDossier, adminPostToBluesky, adminPurgeExpired, adminRunMigration, adminArchiveOfficial, adminRestoreOfficial,
  adminUpdateOfficialSuccessor,
  adminCancelTask, adminRetryTask, adminResolveStuckTasks, adminDeleteTask,
  adminFetchAgentConfigs, adminUpdateAgentConfig, adminFetchAgentConfigProfiles, adminApplyAgentConfigProfile,
  adminFetchQueueStatus, adminUpdateQueueSettings, adminPauseQueue, adminResumeQueue,
  adminProcessQueue, adminDrainQueue,
  adminFetchContributions, adminFetchContributionStats, adminReviewContribution,
  adminFetchFreshnessStatus, adminFetchTurnoverPreview, adminRunFreshness,
  adminFetchNewsletterSummary, adminFetchNewsletterSubscribers, adminFetchNewsletterIssues,
  adminGenerateNewsletter, adminSendNewsletterTest, adminRunWeeklyNewsletter,
  partyBg, personTypeBadge, personTypeLabel,
  type Official, type OfficialLifecycleStatus, type AdminStats, type AgentTask, type DossierStatus, type OfficialsFilters,
  type AgentConfigEntry, type AgentCostProfileEntry, type AgentCostProfileName, type QueueStatusData, type QueueSettings,
  type ContributionReview, type ContributionReviewStats,
  type FreshnessStatusResponse, type FreshnessRunResult, type FreshnessTurnoverPreviewResponse,
  type NewsletterSummary, type NewsletterIssue, type NewsletterSubscriber,
} from './apiClient';
import AddOfficialForm from './src/components/AddOfficialForm';
import OfficialAvatar from './src/components/OfficialAvatar';
import { buildBulkArchiveRequests } from './adminTurnover';
import { buildSnapshotReviewSummary, buildSuccessorReviewSummary } from './adminArchiveReview';
import type { BulkArchiveRequest } from './adminTurnover';

// ============================================================
// ROLE CONFIGURATION — agents & labels per person_type
// ============================================================

interface RoleConfig {
  personType: string;
  label: string;
  singular: string;
  agentTypes: string[];
  agentLabels: Record<string, string>;
  color: string;
  showParty: boolean;
  showLevel: boolean;
}

const ROLE_CONFIGS: RoleConfig[] = [
  {
    personType: 'official',
    label: 'Officials',
    singular: 'Official',
    agentTypes: ['briefing', 'voting_record', 'public_record', 'financial', 'social_intel', 'geo_impact', 'profile_photo', 'accountability_score'],
    agentLabels: { briefing: 'Brief', voting_record: 'Votes', public_record: 'Public', financial: 'Finance', social_intel: 'Social', geo_impact: 'Geo', profile_photo: 'Photo', accountability_score: 'Score' },
    color: 'bg-blue-500',
    showParty: true,
    showLevel: true,
  },
  {
    personType: 'corporate',
    label: 'Executives',
    singular: 'Executive',
    agentTypes: ['ceo_briefing', 'ceo_corporate_disclosure', 'ceo_political_contributions', 'ceo_profile_photo', 'public_record', 'social_intel', 'accountability_score'],
    agentLabels: { ceo_briefing: 'Brief', ceo_corporate_disclosure: 'Corp Disc', ceo_political_contributions: 'Donations', ceo_profile_photo: 'Photo', public_record: 'Public', social_intel: 'Social', accountability_score: 'Score' },
    color: 'bg-orange-500',
    showParty: false,
    showLevel: false,
  },
  {
    personType: 'lobbyist',
    label: 'Lobbyists',
    singular: 'Lobbyist',
    agentTypes: ['briefing', 'public_record', 'financial', 'social_intel', 'accountability_score'],
    agentLabels: { briefing: 'Brief', public_record: 'Public', financial: 'Finance', social_intel: 'Social', accountability_score: 'Score' },
    color: 'bg-violet-500',
    showParty: false,
    showLevel: false,
  },
  {
    personType: 'donor',
    label: 'Donors',
    singular: 'Donor',
    agentTypes: ['briefing', 'financial', 'public_record', 'social_intel', 'accountability_score'],
    agentLabels: { briefing: 'Brief', financial: 'Finance', public_record: 'Public', social_intel: 'Social', accountability_score: 'Score' },
    color: 'bg-emerald-500',
    showParty: true,
    showLevel: false,
  },
  {
    personType: 'media',
    label: 'Media',
    singular: 'Media Figure',
    agentTypes: ['briefing', 'public_record', 'social_intel', 'accountability_score'],
    agentLabels: { briefing: 'Brief', public_record: 'Public', social_intel: 'Social', accountability_score: 'Score' },
    color: 'bg-cyan-500',
    showParty: false,
    showLevel: false,
  },
];

// Merged label map for task queue display
const ALL_AGENT_LABELS: Record<string, string> = {};
for (const rc of ROLE_CONFIGS) {
  Object.assign(ALL_AGENT_LABELS, rc.agentLabels);
}

// ============================================================
// Admin Auth Gate
// ============================================================

function AdminAuth({ onAuth }: { onAuth: (key: string) => void }) {
  const [key, setKey] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!key.trim()) return;
    setChecking(true);
    setError('');
    const valid = await adminVerifyKey(key.trim());
    if (valid) {
      sessionStorage.setItem('aac_admin_key', key.trim());
      onAuth(key.trim());
    } else {
      setError('Invalid admin key');
    }
    setChecking(false);
  };

  return (
    <div className="min-h-screen bg-cream-50 dark:bg-navy-900 flex items-center justify-center">
      <div className="bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded-2xl p-10 w-full max-w-md shadow-2xl">
        <div className="text-center mb-8">
          <div className="inline-block bg-red-600/10 text-red-500 text-[10px] font-black uppercase tracking-[0.3em] px-4 py-2 rounded-sm border border-red-500/20 mb-4">
            Restricted Access
          </div>
          <h1 className="text-3xl font-black text-navy-900 dark:text-cream-50 uppercase tracking-tight">Admin Panel</h1>
          <p className="text-sm text-navy-400 dark:text-navy-300 mt-2">Intelligence Operations Control</p>
        </div>
        <form onSubmit={handleSubmit}>
          <label className="block text-[10px] font-black text-navy-400 dark:text-navy-300 uppercase tracking-widest mb-2">Admin API Key</label>
          <input
            type="password"
            value={key}
            onChange={e => setKey(e.target.value)}
            className="w-full bg-cream-50 dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg px-4 py-3 text-sm text-navy-900 dark:text-cream-50 focus:border-gold-500 outline-none transition"
            placeholder="Enter your admin key"
            autoFocus
          />
          {error && <p className="text-red-500 text-xs font-bold mt-2">{error}</p>}
          <button
            type="submit"
            disabled={checking}
            className="mt-6 w-full bg-navy-800 dark:bg-gold-500 hover:bg-gold-600 dark:hover:bg-gold-400 text-white dark:text-navy-900 font-black text-xs uppercase tracking-widest py-3 rounded-lg transition disabled:opacity-50"
          >
            {checking ? 'Verifying...' : 'Authenticate'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// Stats Bar
// ============================================================

function StatsBar({ stats }: { stats: AdminStats }) {
  const totalTasks = Object.values(stats.tasksByStatus).reduce((a, b) => a + b, 0);
  const coverage = stats.totalOfficials > 0
    ? Math.round((stats.officialsWithDossier / stats.totalOfficials) * 100)
    : 0;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div className="bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded-xl p-5">
        <p className="text-[10px] font-black text-navy-400 dark:text-navy-300 uppercase tracking-widest mb-1">Total Tracked</p>
        <p className="text-3xl font-black text-navy-900 dark:text-cream-50">{stats.totalOfficials.toLocaleString()}</p>
      </div>
      <div className="bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded-xl p-5">
        <p className="text-[10px] font-black text-navy-400 dark:text-navy-300 uppercase tracking-widest mb-1">Dossiers Built</p>
        <p className="text-3xl font-black text-navy-900 dark:text-cream-50">{stats.officialsWithDossier.toLocaleString()}</p>
        <p className="text-[10px] text-navy-400 dark:text-navy-300 font-bold">{coverage}% coverage</p>
      </div>
      <div className="bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded-xl p-5">
        <p className="text-[10px] font-black text-navy-400 dark:text-navy-300 uppercase tracking-widest mb-1">Tasks Run</p>
        <p className="text-3xl font-black text-navy-900 dark:text-cream-50">{totalTasks}</p>
        <div className="flex gap-2 mt-1 flex-wrap">
          {stats.tasksByStatus.completed && <span className="text-[9px] font-bold text-green-500">{stats.tasksByStatus.completed} done</span>}
          {stats.tasksByStatus.failed && <span className="text-[9px] font-bold text-red-500">{stats.tasksByStatus.failed} failed</span>}
          {stats.tasksByStatus.running && <span className="text-[9px] font-bold text-yellow-500">{stats.tasksByStatus.running} running</span>}
          {stats.tasksByStatus.pending && <span className="text-[9px] font-bold text-orange-400">{stats.tasksByStatus.pending} pending</span>}
        </div>
      </div>
      <div className="bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded-xl p-5">
        <p className="text-[10px] font-black text-navy-400 dark:text-navy-300 uppercase tracking-widest mb-1">Cache Sections</p>
        <div className="flex flex-wrap gap-1 mt-2">
          {stats.cacheByType && Object.entries(stats.cacheByType).map(([type, count]) => (
            <span key={type} className="text-[8px] font-bold bg-cream-200 dark:bg-navy-700 text-navy-500 dark:text-navy-300 px-2 py-0.5 rounded">
              {type}: {count}
            </span>
          ))}
          {(!stats.cacheByType || Object.keys(stats.cacheByType).length === 0) && <span className="text-[9px] text-navy-400 dark:text-navy-300">No cache yet</span>}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Dossier Status Badges (role-aware)
// ============================================================

function DossierStatusBadges({ status, agentTypes, agentLabels }: { status: DossierStatus | null; agentTypes: string[]; agentLabels: Record<string, string> }) {
  if (!status) return <span className="text-[9px] text-navy-400 dark:text-navy-300">Loading...</span>;

  const sections = Object.keys(status.sections);
  if (sections.length === 0) {
    return <span className="text-[9px] text-navy-400 dark:text-navy-300 font-bold">No dossier</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {agentTypes.map(type => {
        const s = status.sections[type];
        if (!s) return null;
        return (
          <span
            key={type}
            className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${
              s.fresh ? 'bg-green-900/40 text-green-400' : 'bg-yellow-900/40 text-yellow-400'
            }`}
            title={`${type}: ${s.fresh ? 'fresh' : 'expired'}`}
          >
            {agentLabels[type] || type}
          </span>
        );
      })}
    </div>
  );
}

function hasDossierData(status: DossierStatus | null): boolean {
  if (!status) return false;
  return Object.keys(status.sections).length > 0;
}

// ============================================================
// Role Manager — manages a single person_type with its agents
// ============================================================

function RoleManager({ adminKey, config }: { adminKey: string; config: RoleConfig }) {
  const [officials, setOfficials] = useState<Official[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<OfficialsFilters>({ limit: 25, person_type: config.personType, lifecycle_status: 'active' });
  const [search, setSearch] = useState('');
  const [dossierStatuses, setDossierStatuses] = useState<Record<string, DossierStatus | null>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [building, setBuilding] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState<Set<string>>(new Set());
  const [postingBsky, setPostingBsky] = useState<Set<string>>(new Set());
  const [bskyResults, setBskyResults] = useState<Record<string, string>>({});
  const [buildResults, setBuildResults] = useState<Record<string, string>>({});
  const [showAddForm, setShowAddForm] = useState(false);

  const loadOfficials = useCallback(async () => {
    const res = await fetchOfficials({ ...filters, person_type: config.personType, page, search: search || undefined });
    setOfficials(res.officials);
    setTotalPages(res.pagination.totalPages);
  }, [filters, page, search, config.personType]);

  useEffect(() => { loadOfficials(); }, [loadOfficials]);

  useEffect(() => {
    const ids = officials.map(o => o.id).filter(id => !(id in dossierStatuses));
    if (ids.length === 0) return;
    ids.forEach(async (id) => {
      try {
        const status = await fetchDossierStatus(id);
        setDossierStatuses(prev => ({ ...prev, [id]: status }));
      } catch {
        setDossierStatuses(prev => ({ ...prev, [id]: null }));
      }
    });
  }, [officials]);

  // Reset pagination when switching roles
  useEffect(() => {
    setPage(1);
    setSelected(new Set());
    setBuildResults({});
    setBskyResults({});
    setDossierStatuses({});
  }, [config.personType]);

  const handleBuild = async (officialId: string, force = false) => {
    setBuilding(prev => new Set(prev).add(officialId));
    setBuildResults(prev => ({ ...prev, [officialId]: force ? 'rebuilding...' : 'building...' }));
    try {
      const result = await adminBuildDossier(adminKey, officialId, force);
      setBuildResults(prev => ({ ...prev, [officialId]: result.status === 'all_cached' ? 'cached' : 'done' }));
      const status = await fetchDossierStatus(officialId);
      setDossierStatuses(prev => ({ ...prev, [officialId]: status }));
    } catch (err: any) {
      setBuildResults(prev => ({ ...prev, [officialId]: `error: ${err.message}` }));
    } finally {
      setBuilding(prev => { const s = new Set(prev); s.delete(officialId); return s; });
    }
  };

  const handleDelete = async (officialId: string, officialName: string) => {
    if (!confirm(`Delete all dossier data for ${officialName}?`)) return;
    setDeleting(prev => new Set(prev).add(officialId));
    setBuildResults(prev => ({ ...prev, [officialId]: 'deleting...' }));
    try {
      await adminDeleteDossier(adminKey, officialId);
      setBuildResults(prev => ({ ...prev, [officialId]: 'deleted' }));
      const status = await fetchDossierStatus(officialId);
      setDossierStatuses(prev => ({ ...prev, [officialId]: status }));
    } catch (err: any) {
      setBuildResults(prev => ({ ...prev, [officialId]: `error: ${err.message}` }));
    } finally {
      setDeleting(prev => { const s = new Set(prev); s.delete(officialId); return s; });
    }
  };

  const handlePostToBluesky = async (officialId: string) => {
    setPostingBsky(prev => new Set(prev).add(officialId));
    setBskyResults(prev => ({ ...prev, [officialId]: 'posting...' }));
    try {
      await adminPostToBluesky(adminKey, officialId);
      setBskyResults(prev => ({ ...prev, [officialId]: 'posted' }));
    } catch (err: any) {
      setBskyResults(prev => ({ ...prev, [officialId]: `error: ${err.message}` }));
    } finally {
      setPostingBsky(prev => { const s = new Set(prev); s.delete(officialId); return s; });
    }
  };

  const handleBatchBuild = async (force = false) => {
    if (selected.size === 0) return;
    const ids = Array.from(selected) as string[];
    ids.forEach(id => {
      setBuilding(prev => new Set(prev).add(id));
      setBuildResults(prev => ({ ...prev, [id]: 'queued...' }));
    });
    try {
      const result = await adminBuildBatch(adminKey, ids, force);
      for (const [id, agents] of Object.entries(result.enqueued) as [string, string[]][]) {
        setBuildResults(prev => ({ ...prev, [id]: `queued: ${agents.length} agents` }));
        fetchDossierStatus(id).then(status => setDossierStatuses(prev => ({ ...prev, [id]: status }))).catch(() => {});
      }
      for (const id of Object.keys(result.skipped)) {
        setBuildResults(prev => ({ ...prev, [id]: 'skipped (fresh)' }));
      }
    } catch (err: any) {
      ids.forEach(id => setBuildResults(prev => ({ ...prev, [id]: `error: ${err.message}` })));
    } finally {
      setBuilding(new Set());
    }
    setSelected(new Set());
  };

  const handleBuildMissing = async (force = false) => {
    setBuildResults(prev => ({ ...prev, __bulk__: force ? 'queuing forced rebuilds...' : 'queuing missing dossiers...' }));
    try {
      const result = await adminBuildMissingDossiers(adminKey, {
        person_type: config.personType,
        state: filters.state,
        party: filters.party,
        level: filters.level,
        search: search || undefined,
        force,
      });
      for (const [id, agents] of Object.entries(result.enqueued) as [string, string[]][]) {
        setBuildResults(prev => ({ ...prev, [id]: `queued: ${agents.length} agents`, __bulk__: result.message }));
      }
      for (const id of Object.keys(result.skipped)) {
        setBuildResults(prev => ({ ...prev, [id]: 'skipped (fresh)', __bulk__: result.message }));
      }
      await loadOfficials();
    } catch (err: any) {
      setBuildResults(prev => ({ ...prev, __bulk__: `error: ${err.message}` }));
    }
  };

  const handleArchiveToggle = async (official: Official) => {
    const isArchived = official.lifecycle_status === 'archived';
    const reasonInput = isArchived ? undefined : prompt(`Archive reason for ${official.name}`, 'replaced_after_election');
    if (!isArchived && reasonInput === null) return;
    const reason = isArchived ? undefined : (reasonInput?.trim() || 'archived_by_admin');
    const successorInput = isArchived
      ? null
      : prompt(`Optional successor official ID for ${official.name}`, official.replaced_by_official_id || '');
    if (!isArchived && successorInput === null) return;
    const replaced_by_official_id = isArchived ? undefined : (successorInput?.trim() || null);
    if (!isArchived && !reason) return;
    setBuilding(prev => new Set(prev).add(official.id));
    setBuildResults(prev => ({ ...prev, [official.id]: isArchived ? 'restoring...' : 'archiving...' }));
    try {
      if (isArchived) await adminRestoreOfficial(adminKey, official.id);
      else await adminArchiveOfficial(adminKey, official.id, { reason, replaced_by_official_id });
      await loadOfficials();
      setBuildResults(prev => ({
        ...prev,
        [official.id]: isArchived
          ? 'restored'
          : replaced_by_official_id
            ? `archived → successor ${replaced_by_official_id}`
            : 'archived',
      }));
    } catch (err: any) {
      setBuildResults(prev => ({ ...prev, [official.id]: `error: ${err.message}` }));
    } finally {
      setBuilding(prev => { const s = new Set(prev); s.delete(official.id); return s; });
    }
  };

  const handleBatchDelete = async () => {
    if (selected.size === 0) return;
    if (!confirm(`Delete all dossier data for ${selected.size} ${config.label.toLowerCase()}?`)) return;
    const ids = Array.from(selected) as string[];
    for (const id of ids) {
      setDeleting(prev => new Set(prev).add(id));
      setBuildResults(prev => ({ ...prev, [id]: 'deleting...' }));
      try {
        await adminDeleteDossier(adminKey, id);
        setBuildResults(prev => ({ ...prev, [id]: 'deleted' }));
        const status = await fetchDossierStatus(id);
        setDossierStatuses(prev => ({ ...prev, [id]: status }));
      } catch (err: any) {
        setBuildResults(prev => ({ ...prev, [id]: `error: ${err.message}` }));
      } finally {
        setDeleting(prev => { const s = new Set(prev); s.delete(id); return s; });
      }
    }
    setSelected(new Set());
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  };

  const toggleSelectAll = () => {
    setSelected(selected.size === officials.length ? new Set() : new Set(officials.map(o => o.id)));
  };

  return (
    <div>
      {/* Header with role indicator */}
      <div className="flex items-center gap-3 mb-5">
        <div className={`w-2.5 h-2.5 rounded-full ${config.color}`} />
        <h3 className="text-sm font-black text-navy-900 dark:text-cream-50 uppercase tracking-widest">{config.label} Management</h3>
        <span className="text-[10px] text-navy-400 dark:text-navy-300 font-bold">
          {config.agentTypes.length} agents assigned
        </span>
      </div>

      {/* Agent types for this role */}
      <div className="flex flex-wrap gap-1.5 mb-5">
        {config.agentTypes.map(a => (
          <span key={a} className="text-[8px] font-bold bg-cream-200 dark:bg-navy-700 text-navy-600 dark:text-cream-200 px-2 py-0.5 rounded">
            {config.agentLabels[a] || a}
          </span>
        ))}
      </div>

      {/* Add Button */}
      <div className="mb-5">
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white text-[10px] font-black uppercase tracking-wider rounded-lg transition flex items-center gap-2"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {showAddForm ? 'Hide Form' : `Add ${config.singular}`}
        </button>
      </div>

      {showAddForm && (
        <div className="mb-5">
          <AddOfficialForm adminKey={adminKey} onSuccess={() => { setShowAddForm(false); loadOfficials(); }} onCancel={() => setShowAddForm(false)} />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <input
          type="text"
          placeholder={`Search ${config.label.toLowerCase()}...`}
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          className="bg-cream-50 dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg px-4 py-2 text-sm text-navy-900 dark:text-cream-50 focus:border-gold-500 outline-none transition w-64"
        />
        {config.showLevel && (
          <select
            value={filters.level || ''}
            onChange={e => { setFilters(f => ({ ...f, level: e.target.value || undefined })); setPage(1); }}
            className="bg-cream-50 dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg px-3 py-2 text-sm text-navy-900 dark:text-cream-50 focus:border-gold-500 outline-none"
          >
            <option value="">All Levels</option>
            <option value="federal">Federal</option>
            <option value="state">State</option>
            <option value="local">Local</option>
          </select>
        )}
        {config.showParty && (
          <select
            value={filters.party || ''}
            onChange={e => { setFilters(f => ({ ...f, party: e.target.value || undefined })); setPage(1); }}
            className="bg-cream-50 dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg px-3 py-2 text-sm text-navy-900 dark:text-cream-50 focus:border-gold-500 outline-none"
          >
            <option value="">All Parties</option>
            <option value="Republican">Republican</option>
            <option value="Democrat">Democrat</option>
            <option value="Independent">Independent</option>
          </select>
        )}
        <select
          value={filters.lifecycle_status || 'active'}
          onChange={e => {
            const value = e.target.value;
            setFilters(f => ({ ...f, lifecycle_status: value === 'all' ? undefined : value as OfficialLifecycleStatus, include_archived: value === 'all' }));
            setPage(1);
          }}
          className="bg-cream-50 dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg px-3 py-2 text-sm text-navy-900 dark:text-cream-50 focus:border-gold-500 outline-none"
        >
          <option value="active">Active Only</option>
          <option value="vying">Vying Only</option>
          <option value="inactive">Inactive Only</option>
          <option value="archived">Archived Only</option>
          <option value="all">All Lifecycle States</option>
        </select>
        <select
          value={filters.state || ''}
          onChange={e => { setFilters(f => ({ ...f, state: e.target.value || undefined })); setPage(1); }}
          className="bg-cream-50 dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg px-3 py-2 text-sm text-navy-900 dark:text-cream-50 focus:border-gold-500 outline-none"
        >
          <option value="">All States</option>
          {['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'].map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <button onClick={() => handleBuildMissing(false)} className="text-[10px] font-black uppercase tracking-widest bg-navy-800 dark:bg-gold-500 hover:bg-navy-700 dark:hover:bg-gold-400 text-white dark:text-navy-900 px-4 py-2 rounded transition">
          Build Missing Matches
        </button>
        <button onClick={() => handleBuildMissing(true)} className="text-[10px] font-black uppercase tracking-widest bg-cream-300 dark:bg-navy-600 hover:bg-cream-400 dark:hover:bg-navy-500 text-navy-700 dark:text-cream-200 px-4 py-2 rounded transition">
          Force Match Rebuild
        </button>
      </div>
      {buildResults.__bulk__ && (
        <div className="mb-4 text-xs font-bold text-navy-500 dark:text-navy-300">{buildResults.__bulk__}</div>
      )}

      {/* Batch Actions */}
      {selected.size > 0 && (
        <div className="bg-cream-200 dark:bg-navy-700 border border-cream-300 dark:border-navy-600 rounded-lg p-3 mb-4 flex items-center gap-4 flex-wrap">
          <span className="text-xs font-bold text-navy-900 dark:text-cream-50">{selected.size} selected</span>
          <button onClick={() => handleBatchBuild(false)} className="text-[10px] font-black uppercase tracking-widest bg-navy-800 dark:bg-gold-500 hover:bg-navy-700 dark:hover:bg-gold-400 text-white dark:text-navy-900 px-4 py-2 rounded transition">
            Build Dossiers
          </button>
          <button onClick={() => handleBatchBuild(true)} className="text-[10px] font-black uppercase tracking-widest bg-cream-300 dark:bg-navy-600 hover:bg-cream-400 dark:hover:bg-navy-500 text-navy-700 dark:text-cream-200 px-4 py-2 rounded transition">
            Force Rebuild
          </button>
          <button onClick={handleBatchDelete} className="text-[10px] font-black uppercase tracking-widest bg-red-600/20 hover:bg-red-600 text-red-500 hover:text-white px-4 py-2 rounded transition">
            Delete Dossiers
          </button>
          <button onClick={() => setSelected(new Set())} className="text-[10px] font-bold text-navy-400 dark:text-navy-300 hover:text-navy-900 dark:hover:text-cream-50 transition ml-auto">
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-cream-300 dark:border-navy-600">
              <th className="p-3 w-10">
                <input type="checkbox" checked={selected.size === officials.length && officials.length > 0} onChange={toggleSelectAll} className="accent-gold-500" />
              </th>
              <th className="p-3 text-[10px] font-black text-navy-400 dark:text-navy-300 uppercase tracking-widest">{config.singular}</th>
              {config.showParty && <th className="p-3 text-[10px] font-black text-navy-400 dark:text-navy-300 uppercase tracking-widest">Party</th>}
              <th className="p-3 text-[10px] font-black text-navy-400 dark:text-navy-300 uppercase tracking-widest">State</th>
              <th className="p-3 text-[10px] font-black text-navy-400 dark:text-navy-300 uppercase tracking-widest">Score</th>
              <th className="p-3 text-[10px] font-black text-navy-400 dark:text-navy-300 uppercase tracking-widest">Dossier</th>
              <th className="p-3 text-[10px] font-black text-navy-400 dark:text-navy-300 uppercase tracking-widest text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-cream-200 dark:divide-navy-700/50">
            {officials.map(o => {
              const has = hasDossierData(dossierStatuses[o.id] ?? null);
              const isBusy = building.has(o.id) || deleting.has(o.id);
              return (
                <tr key={o.id} className="hover:bg-cream-100 dark:hover:bg-navy-700/30 transition">
                  <td className="p-3">
                    <input type="checkbox" checked={selected.has(o.id)} onChange={() => toggleSelect(o.id)} className="accent-gold-500" />
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <OfficialAvatar
                        name={o.name}
                        photoUrl={o.photo_url}
                        className="w-8 h-8 rounded-full bg-cream-200 dark:bg-navy-700 flex items-center justify-center"
                        imgClassName="w-8 h-8 rounded-full object-cover bg-cream-200 dark:bg-navy-700"
                        initialsClassName="text-[9px] font-black text-navy-400 dark:text-navy-300"
                      />
                      <div>
                        <p className="text-sm font-bold text-navy-900 dark:text-cream-50">{o.name} {o.lifecycle_status === 'archived' && <span className="ml-2 text-[9px] font-black uppercase tracking-wider text-orange-400">Archived</span>}</p>
                        <p className="text-[10px] text-navy-400 dark:text-navy-300 truncate max-w-[200px]">{o.organization || o.office_name}</p>
                      </div>
                    </div>
                  </td>
                  {config.showParty && (
                    <td className="p-3">
                      {o.party ? <span className={`text-[9px] font-bold px-2 py-0.5 rounded border ${partyBg(o.party)}`}>{o.party}</span> : <span className="text-navy-400 dark:text-navy-300 text-xs">-</span>}
                    </td>
                  )}
                  <td className="p-3 text-xs font-bold text-navy-500 dark:text-navy-300">{o.state || '-'}</td>
                  <td className="p-3">
                    {o.accountability_score != null ? (
                      <span className={`text-sm font-black ${o.accountability_score >= 70 ? 'text-green-400' : o.accountability_score >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                        {o.accountability_score}
                      </span>
                    ) : (
                      <span className="text-navy-400 dark:text-navy-300 text-xs">--</span>
                    )}
                  </td>
                  <td className="p-3">
                    <DossierStatusBadges status={dossierStatuses[o.id] ?? null} agentTypes={config.agentTypes} agentLabels={config.agentLabels} />
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {buildResults[o.id] && (
                        <span className={`text-[9px] font-bold ${
                          buildResults[o.id] === 'done' || buildResults[o.id] === 'cached' ? 'text-green-400' :
                          buildResults[o.id] === 'deleted' ? 'text-orange-400' :
                          buildResults[o.id]?.startsWith('error') ? 'text-red-400' : 'text-yellow-400'
                        }`}>{buildResults[o.id]}</span>
                      )}
                      <button
                        onClick={() => handleBuild(o.id, has)}
                        disabled={isBusy}
                        className="text-[9px] font-black uppercase tracking-wider bg-navy-800/20 dark:bg-gold-500/20 hover:bg-navy-800 dark:hover:bg-gold-500 text-navy-700 dark:text-gold-400 hover:text-white dark:hover:text-navy-900 px-3 py-1.5 rounded transition disabled:opacity-50"
                        title={has ? 'Rebuild dossier' : 'Build new dossier'}
                      >
                        {isBusy ? (building.has(o.id) ? '...' : 'Del...') : has ? 'Rebuild' : 'Build'}
                      </button>
                      {has && (
                        <button onClick={() => handleBuild(o.id, false)} disabled={isBusy}
                          className="text-[9px] font-black uppercase tracking-wider bg-cream-200 dark:bg-navy-700 hover:bg-cream-300 dark:hover:bg-navy-600 text-navy-500 dark:text-navy-300 px-3 py-1.5 rounded transition disabled:opacity-50"
                          title="Refresh expired agents only">
                          Refresh
                        </button>
                      )}
                      {has && (
                        <button onClick={() => handleDelete(o.id, o.name)} disabled={isBusy}
                          className="text-[9px] font-black uppercase tracking-wider bg-red-600/10 hover:bg-red-600 text-red-400 hover:text-white px-3 py-1.5 rounded transition disabled:opacity-50">
                          Delete
                        </button>
                      )}
                      <button onClick={() => handleArchiveToggle(o)} disabled={isBusy}
                        className="text-[9px] font-black uppercase tracking-wider bg-orange-500/10 hover:bg-orange-500 text-orange-400 hover:text-white px-3 py-1.5 rounded transition disabled:opacity-50">
                        {o.lifecycle_status === 'archived' ? 'Restore' : 'Archive'}
                      </button>
                      {has && (
                        <button onClick={() => handlePostToBluesky(o.id)} disabled={isBusy || postingBsky.has(o.id)}
                          className="text-[9px] font-black uppercase tracking-wider bg-blue-500/10 hover:bg-blue-500 text-blue-400 hover:text-white px-3 py-1.5 rounded transition disabled:opacity-50">
                          {postingBsky.has(o.id) ? '...' : bskyResults[o.id] === 'posted' ? 'Repost' : 'Bsky'}
                        </button>
                      )}
                      {bskyResults[o.id] && (
                        <span className={`text-[9px] font-bold ${bskyResults[o.id] === 'posted' ? 'text-blue-400' : bskyResults[o.id]?.startsWith('error') ? 'text-red-400' : 'text-yellow-400'}`}>
                          {bskyResults[o.id]}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {officials.length === 0 && (
              <tr><td colSpan={config.showParty ? 7 : 6} className="p-12 text-center text-sm text-navy-400 dark:text-navy-300">No {config.label.toLowerCase()} found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            className="text-[10px] font-black uppercase tracking-widest bg-cream-200 dark:bg-navy-700 hover:bg-cream-300 dark:hover:bg-navy-600 text-navy-700 dark:text-cream-200 px-4 py-2 rounded transition disabled:opacity-30">
            Previous
          </button>
          <span className="text-xs font-bold text-navy-400 dark:text-navy-300">Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            className="text-[10px] font-black uppercase tracking-widest bg-cream-200 dark:bg-navy-700 hover:bg-cream-300 dark:hover:bg-navy-600 text-navy-700 dark:text-cream-200 px-4 py-2 rounded transition disabled:opacity-30">
            Next
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Contributor Review Panel
// ============================================================

const CONTRIBUTION_TYPE_LABELS: Record<string, string> = {
  accountability_record: 'Accountability Record',
  document: 'Document',
  news_link: 'News Link',
  social_post: 'Social Post',
  other: 'Other',
};

function ContributorReviewPanel({ adminKey }: { adminKey: string }) {
  const [contributions, setContributions] = useState<ContributionReview[]>([]);
  const [stats, setStats] = useState<ContributionReviewStats | null>(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<Set<string>>(new Set());
  const [reviewNotes, setReviewNotes] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [contribRes, statsRes] = await Promise.all([
        adminFetchContributions(adminKey, statusFilter),
        adminFetchContributionStats(adminKey),
      ]);
      setContributions(contribRes.contributions);
      setStats(statsRes);
    } catch (err: any) {
      setMessage({ text: `Failed to load: ${err.message}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [adminKey, statusFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleReview = async (id: string, status: 'approved' | 'rejected' | 'flagged') => {
    setActing(prev => new Set(prev).add(id));
    setMessage(null);
    try {
      await adminReviewContribution(adminKey, id, status, reviewNotes[id]);
      setMessage({ text: `Contribution ${status}`, type: 'success' });
      await loadData();
    } catch (err: any) {
      setMessage({ text: `Review failed: ${err.message}`, type: 'error' });
    } finally {
      setActing(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  const toggleExpand = (id: string) => {
    setExpanded(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <h3 className="text-sm font-black text-navy-900 dark:text-cream-50 uppercase tracking-widest">Contributor Review</h3>
      </div>

      {/* Stats counters */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {[
            { label: 'Pending', value: stats.byStatus.pending || 0, color: 'text-amber-600 dark:text-amber-400' },
            { label: 'Approved', value: stats.byStatus.approved || 0, color: 'text-emerald-600 dark:text-emerald-400' },
            { label: 'Rejected', value: stats.byStatus.rejected || 0, color: 'text-red-600 dark:text-red-400' },
            { label: 'Total', value: stats.total, color: 'text-navy-600 dark:text-cream-200' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-cream-100 dark:bg-navy-700 rounded-lg p-3 text-center">
              <div className={`text-2xl font-black ${color}`}>{value}</div>
              <div className="text-[9px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest mt-1">{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filter bar */}
      <div className="flex items-center gap-3 mb-4">
        {['pending', 'approved', 'rejected', 'flagged'].map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`text-[10px] font-black uppercase tracking-widest px-4 py-2 rounded-lg transition ${
              statusFilter === s
                ? 'bg-navy-800 dark:bg-gold-500 text-white dark:text-navy-900'
                : 'bg-cream-200 dark:bg-navy-700 text-navy-500 dark:text-navy-300 hover:bg-cream-300 dark:hover:bg-navy-600'
            }`}
          >
            {s} {stats ? `(${stats.byStatus[s] || 0})` : ''}
          </button>
        ))}
        <button onClick={loadData} className="text-[10px] font-black uppercase tracking-widest bg-cream-200 dark:bg-navy-700 hover:bg-cream-300 dark:hover:bg-navy-600 text-navy-700 dark:text-cream-200 px-4 py-2 rounded-lg transition ml-auto">
          Refresh
        </button>
      </div>

      {message && (
        <div className={`rounded-lg p-3 mb-4 text-xs font-bold ${message.type === 'success' ? 'bg-green-600/10 border border-green-500/30 text-green-400' : 'bg-red-600/10 border border-red-500/30 text-red-400'}`}>
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-navy-400 dark:text-navy-300 text-sm">Loading contributions...</div>
      ) : contributions.length === 0 ? (
        <div className="text-center py-12 text-navy-400 dark:text-navy-300 text-sm">No {statusFilter} contributions found.</div>
      ) : (
        <div className="space-y-3">
          {contributions.map(c => {
            const busy = acting.has(c.id);
            const isExpanded = expanded.has(c.id);
            return (
              <div key={c.id} className="bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded-xl overflow-hidden">
                {/* Summary row */}
                <div className="p-4 flex items-center gap-4 cursor-pointer hover:bg-cream-50 dark:hover:bg-navy-700/30 transition" onClick={() => toggleExpand(c.id)}>
                  {/* Official info */}
                  <div className="flex items-center gap-2 min-w-[160px]">
                    {c.officialPhoto ? (
                      <img src={c.officialPhoto} alt="" className="w-7 h-7 rounded-full object-cover bg-cream-200 dark:bg-navy-700" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-cream-200 dark:bg-navy-700 flex items-center justify-center text-[8px] font-black text-navy-400 dark:text-navy-300">?</div>
                    )}
                    <div>
                      <p className="text-xs font-bold text-navy-900 dark:text-cream-50">{c.officialName}</p>
                      <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded border ${personTypeBadge(c.officialType)}`}>
                        {personTypeLabel(c.officialType)}
                      </span>
                    </div>
                  </div>

                  {/* Contribution type */}
                  <span className="text-[9px] font-black uppercase tracking-wider bg-cream-200 dark:bg-navy-700 text-navy-600 dark:text-cream-200 px-2 py-1 rounded">
                    {CONTRIBUTION_TYPE_LABELS[c.contributionType] || c.contributionType}
                  </span>

                  {/* Title */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-navy-900 dark:text-cream-50 truncate">{c.title}</p>
                  </div>

                  {/* Contributor */}
                  <div className="text-right shrink-0 min-w-[90px]">
                    <p className="text-[10px] font-bold text-navy-500 dark:text-navy-300">{c.contributor.displayName || c.contributor.username}</p>
                    <p className="text-[9px] text-navy-400 dark:text-navy-300">{new Date(c.createdAt).toLocaleDateString()}</p>
                  </div>

                  {/* Votes */}
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[9px] font-bold text-green-500">{c.upvotes} up</span>
                    <span className="text-[9px] font-bold text-red-400">{c.downvotes} dn</span>
                  </div>

                  {/* Expand indicator */}
                  <svg className={`w-4 h-4 text-navy-400 dark:text-navy-300 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="border-t border-cream-200 dark:border-navy-700 p-4 space-y-3">
                    <div>
                      <p className="text-[10px] font-black text-navy-400 dark:text-navy-300 uppercase tracking-widest mb-1">Description</p>
                      <p className="text-xs text-navy-700 dark:text-cream-200 whitespace-pre-wrap">{c.description}</p>
                    </div>

                    {c.sourceUrl && (
                      <div>
                        <p className="text-[10px] font-black text-navy-400 dark:text-navy-300 uppercase tracking-widest mb-1">Source</p>
                        <a href={c.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline break-all">{c.sourceUrl}</a>
                      </div>
                    )}

                    {c.evidenceUrls.length > 0 && (
                      <div>
                        <p className="text-[10px] font-black text-navy-400 dark:text-navy-300 uppercase tracking-widest mb-1">Evidence ({c.evidenceUrls.length})</p>
                        <div className="space-y-1">
                          {c.evidenceUrls.map((url, i) => (
                            <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block text-xs text-blue-500 hover:underline break-all">{url}</a>
                          ))}
                        </div>
                      </div>
                    )}

                    {c.reviewNotes && (
                      <div>
                        <p className="text-[10px] font-black text-navy-400 dark:text-navy-300 uppercase tracking-widest mb-1">Review Notes</p>
                        <p className="text-xs text-navy-600 dark:text-cream-200 italic">{c.reviewNotes}</p>
                      </div>
                    )}

                    {/* Review actions */}
                    {statusFilter === 'pending' && (
                      <div className="border-t border-cream-200 dark:border-navy-700 pt-3 space-y-2">
                        <textarea
                          value={reviewNotes[c.id] || ''}
                          onChange={e => setReviewNotes(prev => ({ ...prev, [c.id]: e.target.value }))}
                          placeholder="Review notes (optional)..."
                          className="w-full bg-cream-50 dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg px-3 py-2 text-xs text-navy-900 dark:text-cream-50 focus:border-gold-500 outline-none resize-none h-16"
                        />
                        <div className="flex gap-2">
                          <button onClick={() => handleReview(c.id, 'approved')} disabled={busy}
                            className="text-[10px] font-black uppercase tracking-widest bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 rounded-lg transition disabled:opacity-50">
                            {busy ? '...' : 'Approve'}
                          </button>
                          <button onClick={() => handleReview(c.id, 'rejected')} disabled={busy}
                            className="text-[10px] font-black uppercase tracking-widest bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-lg transition disabled:opacity-50">
                            {busy ? '...' : 'Reject'}
                          </button>
                          <button onClick={() => handleReview(c.id, 'flagged')} disabled={busy}
                            className="text-[10px] font-black uppercase tracking-widest bg-amber-600 hover:bg-amber-700 text-white px-5 py-2 rounded-lg transition disabled:opacity-50">
                            {busy ? '...' : 'Flag'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Task Queue
// ============================================================

function TaskQueue({ adminKey }: { adminKey: string }) {
  const [tasks, setTasks] = useState<AgentTask[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<Set<string>>(new Set());
  const [resolving, setResolving] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const loadTasks = useCallback(async () => {
    try {
      const res = await adminFetchTasks(adminKey, statusFilter || undefined, 200);
      setTasks(res.tasks);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [adminKey, statusFilter]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  useEffect(() => {
    const hasActive = tasks.some(t => t.status === 'running' || t.status === 'pending');
    if (!hasActive) return;
    const interval = setInterval(loadTasks, 10000);
    return () => clearInterval(interval);
  }, [tasks, loadTasks]);

  const stuckCount = tasks.filter(t => (t.status === 'pending' || t.status === 'running') && (Date.now() - t.created_at > 10 * 60 * 1000)).length;
  const pendingRunningCount = tasks.filter(t => t.status === 'pending' || t.status === 'running').length;

  const handleCancel = async (taskId: string) => {
    setActing(prev => new Set(prev).add(taskId));
    try { await adminCancelTask(adminKey, taskId); setMessage({ text: 'Task cancelled', type: 'success' }); await loadTasks(); }
    catch (err: any) { setMessage({ text: `Cancel failed: ${err.message}`, type: 'error' }); }
    finally { setActing(prev => { const s = new Set(prev); s.delete(taskId); return s; }); }
  };

  const handleRetry = async (taskId: string) => {
    setActing(prev => new Set(prev).add(taskId)); setMessage(null);
    try { const res = await adminRetryTask(adminKey, taskId); setMessage({ text: `Retry ${res.result}`, type: res.result === 'completed' ? 'success' : 'error' }); await loadTasks(); }
    catch (err: any) { setMessage({ text: `Retry failed: ${err.message}`, type: 'error' }); }
    finally { setActing(prev => { const s = new Set(prev); s.delete(taskId); return s; }); }
  };

  const handleDelete = async (taskId: string) => {
    setActing(prev => new Set(prev).add(taskId));
    try { await adminDeleteTask(adminKey, taskId); setMessage({ text: 'Task deleted', type: 'success' }); await loadTasks(); }
    catch (err: any) { setMessage({ text: `Delete failed: ${err.message}`, type: 'error' }); }
    finally { setActing(prev => { const s = new Set(prev); s.delete(taskId); return s; }); }
  };

  const handleResolveStuck = async () => {
    if (!confirm('Mark all tasks stuck for >10 minutes as failed?')) return;
    setResolving(true); setMessage(null);
    try { const res = await adminResolveStuckTasks(adminKey, 10); setMessage({ text: `Resolved ${res.tasksResolved} stuck tasks`, type: 'success' }); await loadTasks(); }
    catch (err: any) { setMessage({ text: `Resolve failed: ${err.message}`, type: 'error' }); }
    finally { setResolving(false); }
  };

  const statusColor = (s: string) => {
    if (s === 'completed') return 'bg-green-900/40 text-green-400';
    if (s === 'failed') return 'bg-red-900/40 text-red-400';
    if (s === 'running') return 'bg-yellow-900/40 text-yellow-400';
    return 'bg-cream-200 dark:bg-navy-700 text-navy-500 dark:text-navy-300';
  };

  const formatTime = (ts: number | null) => ts ? new Date(ts).toLocaleString() : '-';

  const taskAge = (createdAt: number) => {
    const mins = Math.floor((Date.now() - createdAt) / 60000);
    if (mins < 1) return '<1m';
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ${mins % 60}m`;
    return `${Math.floor(hours / 24)}d ${hours % 24}h`;
  };

  const isStuck = (task: AgentTask) => (task.status === 'pending' || task.status === 'running') && (Date.now() - task.created_at > 10 * 60 * 1000);

  return (
    <div>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="bg-cream-50 dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg px-3 py-2 text-sm text-navy-900 dark:text-cream-50 focus:border-gold-500 outline-none">
          <option value="">All Tasks</option>
          <option value="running">Running</option>
          <option value="pending">Pending</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
        </select>
        <button onClick={loadTasks} className="text-[10px] font-black uppercase tracking-widest bg-cream-200 dark:bg-navy-700 hover:bg-cream-300 dark:hover:bg-navy-600 text-navy-700 dark:text-cream-200 px-4 py-2 rounded transition">Refresh</button>
        {pendingRunningCount > 0 && (
          <button onClick={handleResolveStuck} disabled={resolving}
            className="text-[10px] font-black uppercase tracking-widest bg-orange-600/20 hover:bg-orange-600 text-orange-500 hover:text-white px-4 py-2 rounded transition disabled:opacity-50 ml-auto">
            {resolving ? 'Resolving...' : `Resolve Stuck (${stuckCount > 0 ? stuckCount : pendingRunningCount})`}
          </button>
        )}
      </div>

      {stuckCount > 0 && (
        <div className="bg-orange-600/10 border border-orange-500/30 rounded-lg p-4 mb-4 flex items-center gap-3">
          <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse shrink-0" />
          <p className="text-xs font-bold text-orange-400">
            {stuckCount} task{stuckCount !== 1 ? 's' : ''} stuck for over 10 minutes.
          </p>
        </div>
      )}

      {message && (
        <div className={`rounded-lg p-3 mb-4 text-xs font-bold ${message.type === 'success' ? 'bg-green-600/10 border border-green-500/30 text-green-400' : 'bg-red-600/10 border border-red-500/30 text-red-400'}`}>
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-navy-400 dark:text-navy-300 text-sm">Loading tasks...</div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-12 text-navy-400 dark:text-navy-300 text-sm">No tasks found.</div>
      ) : (
        <div className="space-y-2">
          {tasks.map(task => {
            const stuck = isStuck(task);
            const busy = acting.has(task.id);
            const actionable = task.status === 'pending' || task.status === 'running' || task.status === 'failed';
            return (
              <div key={task.id} className={`bg-white dark:bg-navy-800 border rounded-lg p-4 flex items-center gap-4 ${stuck ? 'border-orange-500/50' : 'border-cream-300 dark:border-navy-600'}`}>
                <div className="flex items-center gap-3 min-w-[180px]">
                  <OfficialAvatar
                    name={task.official_name}
                    photoUrl={task.photo_url}
                    className="w-8 h-8 rounded-full bg-cream-200 dark:bg-navy-700 flex items-center justify-center"
                    imgClassName="w-8 h-8 rounded-full object-cover bg-cream-200 dark:bg-navy-700"
                    initialsClassName="text-[9px] font-black text-navy-400 dark:text-navy-300"
                  />
                  <div>
                    <p className="text-sm font-bold text-navy-900 dark:text-cream-50">{task.official_name || task.official_id}</p>
                    <p className="text-[10px] text-navy-400 dark:text-navy-300">{task.person_type}</p>
                  </div>
                </div>
                <div className="min-w-[70px]">
                  <span className="text-[9px] font-black uppercase tracking-wider bg-cream-200 dark:bg-navy-700 text-navy-600 dark:text-cream-200 px-2 py-1 rounded">
                    {ALL_AGENT_LABELS[task.agent_type] || task.agent_type}
                  </span>
                </div>
                <div className="min-w-[100px]">
                  <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-1 rounded ${statusColor(task.status)}`}>{task.status}</span>
                  {(task.status === 'pending' || task.status === 'running') && (
                    <span className={`ml-1.5 text-[8px] font-bold ${stuck ? 'text-orange-400' : 'text-navy-400 dark:text-navy-300'}`}>{taskAge(task.created_at)}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  {task.error_message ? <p className="text-[10px] text-red-400 truncate" title={task.error_message}>{task.error_message}</p>
                    : task.result_summary ? <p className="text-[10px] text-navy-500 dark:text-navy-300 truncate" title={task.result_summary}>{task.result_summary}</p>
                    : <p className="text-[10px] text-navy-400 dark:text-navy-300">-</p>}
                </div>
                <div className="text-right shrink-0 min-w-[100px]">
                  <p className="text-[9px] text-navy-400 dark:text-navy-300">{formatTime(task.created_at)}</p>
                  {task.completed_at && task.started_at && <p className="text-[8px] text-navy-400 dark:text-navy-200">{((task.completed_at - task.started_at) / 1000).toFixed(1)}s</p>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {(task.status === 'pending' || task.status === 'running') && (
                    <button onClick={() => handleCancel(task.id)} disabled={busy} className="text-[8px] font-black uppercase tracking-wider bg-orange-600/15 hover:bg-orange-600 text-orange-400 hover:text-white px-2.5 py-1 rounded transition disabled:opacity-50">{busy ? '...' : 'Cancel'}</button>
                  )}
                  {actionable && (
                    <button onClick={() => handleRetry(task.id)} disabled={busy} className="text-[8px] font-black uppercase tracking-wider bg-blue-600/15 hover:bg-blue-600 text-blue-400 hover:text-white px-2.5 py-1 rounded transition disabled:opacity-50">{busy ? '...' : 'Retry'}</button>
                  )}
                  {(task.status === 'failed' || task.status === 'completed') && (
                    <button onClick={() => handleDelete(task.id)} disabled={busy} className="text-[8px] font-black uppercase tracking-wider bg-red-600/10 hover:bg-red-600 text-red-400 hover:text-white px-2.5 py-1 rounded transition disabled:opacity-50">{busy ? '...' : 'Del'}</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Queue Panel
// ============================================================

function QueuePanel({ adminKey }: { adminKey: string }) {
  const [status, setStatus] = useState<QueueStatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    try { const s = await adminFetchQueueStatus(adminKey); setStatus(s); } catch { /* ignore */ }
    setLoading(false);
  }, [adminKey]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { const interval = setInterval(loadStatus, 5000); return () => clearInterval(interval); }, [loadStatus]);

  const handleAction = async (action: string) => {
    setActionLoading(action);
    try {
      switch (action) {
        case 'pause': await adminPauseQueue(adminKey); break;
        case 'resume': await adminResumeQueue(adminKey); break;
        case 'process': await adminProcessQueue(adminKey); break;
        case 'drain': if (confirm('Cancel ALL pending tasks?')) await adminDrainQueue(adminKey); break;
      }
      await loadStatus();
    } catch { /* ignore */ }
    setActionLoading(null);
  };

  const handleSettingChange = async (key: keyof QueueSettings, value: number | boolean) => {
    setActionLoading('settings');
    try { await adminUpdateQueueSettings(adminKey, { [key]: value }); await loadStatus(); } catch { /* ignore */ }
    setActionLoading(null);
  };

  if (loading) return <p className="text-[11px] text-navy-400 dark:text-navy-300 animate-pulse">Loading queue status...</p>;
  if (!status) return <p className="text-[11px] text-red-500">Failed to load queue status</p>;

  const { settings } = status;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-navy-900 dark:text-cream-50 uppercase tracking-widest">Task Queue</h3>
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
          settings.paused ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
        }`}>
          <span className={`w-1.5 h-1.5 rounded-full ${settings.paused ? 'bg-amber-500' : 'bg-emerald-500 animate-pulse'}`} />
          {settings.paused ? 'Paused' : 'Active'}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Pending', value: status.pending, color: 'text-amber-600 dark:text-amber-400' },
          { label: 'Running', value: status.running, color: 'text-blue-600 dark:text-blue-400' },
          { label: 'Completed (24h)', value: status.completed_today, color: 'text-emerald-600 dark:text-emerald-400' },
          { label: 'Failed (24h)', value: status.failed_today, color: 'text-red-600 dark:text-red-400' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-cream-100 dark:bg-navy-700 rounded-lg p-3 text-center">
            <div className={`text-2xl font-black ${color}`}>{value}</div>
            <div className="text-[9px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest mt-1">{label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-cream-100 dark:bg-navy-700 rounded-lg p-3 text-center">
          <div className="text-lg font-black text-navy-700 dark:text-cream-200">{status.officials_pending}</div>
          <div className="text-[9px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest">Queued</div>
        </div>
        <div className="bg-cream-100 dark:bg-navy-700 rounded-lg p-3 text-center">
          <div className="text-lg font-black text-navy-700 dark:text-cream-200">{status.officials_running}</div>
          <div className="text-[9px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest">Processing</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {settings.paused ? (
          <button onClick={() => handleAction('resume')} disabled={!!actionLoading} className="px-4 py-2 bg-emerald-600 text-white text-[10px] font-black uppercase tracking-widest rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition">
            {actionLoading === 'resume' ? 'Resuming...' : 'Resume Queue'}
          </button>
        ) : (
          <button onClick={() => handleAction('pause')} disabled={!!actionLoading} className="px-4 py-2 bg-amber-600 text-white text-[10px] font-black uppercase tracking-widest rounded-lg hover:bg-amber-700 disabled:opacity-50 transition">
            {actionLoading === 'pause' ? 'Pausing...' : 'Pause Queue'}
          </button>
        )}
        <button onClick={() => handleAction('process')} disabled={!!actionLoading} className="px-4 py-2 bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest rounded-lg hover:bg-blue-700 disabled:opacity-50 transition">
          {actionLoading === 'process' ? 'Processing...' : 'Process Now'}
        </button>
        <button onClick={() => handleAction('drain')} disabled={!!actionLoading} className="px-4 py-2 bg-red-600 text-white text-[10px] font-black uppercase tracking-widest rounded-lg hover:bg-red-700 disabled:opacity-50 transition">
          {actionLoading === 'drain' ? 'Draining...' : 'Drain Queue'}
        </button>
      </div>

      <div className="space-y-3">
        <h4 className="text-[10px] font-bold text-navy-500 dark:text-navy-300 uppercase tracking-widest">Concurrency Settings</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-cream-100 dark:bg-navy-700 rounded-lg p-3">
            <label className="block text-[9px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest mb-1">Max Parallel</label>
            <select value={settings.max_concurrent_officials} onChange={e => handleSettingChange('max_concurrent_officials', Number(e.target.value))} disabled={!!actionLoading}
              className="w-full bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded px-2 py-1 text-xs text-navy-900 dark:text-cream-50">
              {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="bg-cream-100 dark:bg-navy-700 rounded-lg p-3">
            <label className="block text-[9px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest mb-1">Max Agents</label>
            <select value={settings.max_concurrent_agents} onChange={e => handleSettingChange('max_concurrent_agents', Number(e.target.value))} disabled={!!actionLoading}
              className="w-full bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded px-2 py-1 text-xs text-navy-900 dark:text-cream-50">
              {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="bg-cream-100 dark:bg-navy-700 rounded-lg p-3">
            <label className="block text-[9px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest mb-1">Delay</label>
            <select value={settings.delay_between_officials_ms} onChange={e => handleSettingChange('delay_between_officials_ms', Number(e.target.value))} disabled={!!actionLoading}
              className="w-full bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded px-2 py-1 text-xs text-navy-900 dark:text-cream-50">
              {[0, 1000, 2000, 5000, 10000].map(n => <option key={n} value={n}>{n === 0 ? 'None' : `${n / 1000}s`}</option>)}
            </select>
          </div>
        </div>
      </div>

      {status.recent_batches.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-[10px] font-bold text-navy-500 dark:text-navy-300 uppercase tracking-widest">Recent Batches</h4>
          {status.recent_batches.map(batch => {
            const pct = batch.total > 0 ? Math.round(((batch.completed + batch.failed) / batch.total) * 100) : 0;
            return (
              <div key={batch.batch_id} className="bg-cream-100 dark:bg-navy-700 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-mono text-navy-500 dark:text-navy-300">{batch.batch_id}</span>
                  <span className="text-[10px] font-bold text-navy-400 dark:text-navy-300">{pct}%</span>
                </div>
                <div className="w-full bg-cream-200 dark:bg-navy-800 rounded-full h-1.5 mb-2">
                  <div className="bg-emerald-500 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
                </div>
                <div className="flex gap-3 text-[9px] font-bold uppercase tracking-widest">
                  {batch.pending > 0 && <span className="text-amber-600 dark:text-amber-400">{batch.pending} pending</span>}
                  {batch.running > 0 && <span className="text-blue-600 dark:text-blue-400">{batch.running} running</span>}
                  <span className="text-emerald-600 dark:text-emerald-400">{batch.completed} done</span>
                  {batch.failed > 0 && <span className="text-red-600 dark:text-red-400">{batch.failed} failed</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Agent Config Panel
// ============================================================

const PROVIDER_OPTIONS = [
  { value: 'openrouter', label: 'OpenRouter' },
  { value: 'xai', label: 'x.ai (Grok)' },
  { value: 'anthropic', label: 'Anthropic (Claude)' },
  { value: 'openai', label: 'OpenAI (GPT)' },
];

const MODEL_SUGGESTIONS: Record<string, string[]> = {
  openrouter: ['z-ai/glm-5.2', 'openai/gpt-4o-mini', 'openai/gpt-5-mini', 'x-ai/grok-4.20', 'google/gemini-2.5-pro', '~typesafe/jev-latest'],
  xai: ['grok-4.20', 'grok-4.3'],
  anthropic: ['claude-sonnet-4.6', 'claude-opus-4.1'],
  openai: ['gpt-5-mini', 'gpt-4o-mini', 'gpt-4o'],
};

// Collect all unique agent types across all roles
const UNIQUE_AGENT_TYPES = Array.from(new Set(ROLE_CONFIGS.flatMap(r => r.agentTypes)));

function AgentConfigPanel({ adminKey }: { adminKey: string }) {
  const [configs, setConfigs] = useState<Record<string, AgentConfigEntry>>({});
  const [profiles, setProfiles] = useState<Record<AgentCostProfileName, AgentCostProfileEntry>>({} as Record<AgentCostProfileName, AgentCostProfileEntry>);
  const [defaultProfile, setDefaultProfile] = useState<AgentCostProfileName | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<AgentCostProfileName>('lowest_cost');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [applyingProfile, setApplyingProfile] = useState<AgentCostProfileName | null>(null);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [edits, setEdits] = useState<Record<string, { provider: string; model: string }>>({});

  const loadConfigs = useCallback(async () => {
    try {
      const [res, profileRes] = await Promise.all([
        adminFetchAgentConfigs(adminKey),
        adminFetchAgentConfigProfiles(adminKey),
      ]);
      setConfigs(res.configs);
      setProfiles(profileRes.profiles);
      setDefaultProfile(profileRes.defaultProfile);
      setSelectedProfile(profileRes.defaultProfile);
      const initial: Record<string, { provider: string; model: string }> = {};
      for (const [agent, cfg] of Object.entries(res.configs)) {
        initial[agent] = { provider: cfg.provider, model: cfg.model };
      }
      setEdits(initial);
    } catch (err: any) {
      setMessage({ text: `Failed to load configs: ${err.message}`, type: 'error' });
    } finally { setLoading(false); }
  }, [adminKey]);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  const handleSave = async (agentType: string) => {
    const edit = edits[agentType]; if (!edit) return;
    const current = configs[agentType];
    if (current && edit.provider === current.provider && edit.model === current.model) { setMessage({ text: 'No changes', type: 'success' }); return; }
    setSaving(agentType); setMessage(null);
    try {
      await adminUpdateAgentConfig(adminKey, agentType, { provider: edit.provider, model: edit.model });
      setMessage({ text: `${ALL_AGENT_LABELS[agentType] || agentType} updated`, type: 'success' });
      setConfigs(prev => ({ ...prev, [agentType]: { provider: edit.provider as any, model: edit.model } }));
    } catch (err: any) { setMessage({ text: `Save failed: ${err.message}`, type: 'error' }); }
    finally { setSaving(null); }
  };

  const updateEdit = (agentType: string, field: 'provider' | 'model', value: string) => {
    setEdits(prev => {
      const current = prev[agentType] || { provider: 'openrouter', model: MODEL_SUGGESTIONS.openrouter[0] };
      const updated = { ...current, [field]: value };
      if (field === 'provider' && MODEL_SUGGESTIONS[value]) updated.model = MODEL_SUGGESTIONS[value][0];
      return { ...prev, [agentType]: updated };
    });
  };

  const handleApplyProfile = async () => {
    setApplyingProfile(selectedProfile);
    setMessage(null);
    try {
      const result = await adminApplyAgentConfigProfile(adminKey, selectedProfile);
      setConfigs(result.configs);
      setDefaultProfile(result.defaultProfile);
      const nextEdits: Record<string, { provider: string; model: string }> = {};
      for (const [agent, cfg] of Object.entries(result.configs)) {
        nextEdits[agent] = { provider: cfg.provider, model: cfg.model };
      }
      setEdits(nextEdits);
      setMessage({ text: `Applied ${profiles[selectedProfile]?.label || selectedProfile} to ${result.appliedCount} agents and saved it as the backend default`, type: 'success' });
    } catch (err: any) {
      setMessage({ text: `Profile apply failed: ${err.message}`, type: 'error' });
    } finally {
      setApplyingProfile(null);
    }
  };

  const hasChanges = (agentType: string) => {
    const edit = edits[agentType]; const current = configs[agentType];
    if (!edit || !current) return false;
    return edit.provider !== current.provider || edit.model !== current.model;
  };

  const profileEntries = Object.entries(profiles) as Array<[AgentCostProfileName, AgentCostProfileEntry]>;

  // Group agents by role
  const agentToRoles: Record<string, string[]> = {};
  for (const rc of ROLE_CONFIGS) {
    for (const a of rc.agentTypes) {
      if (!agentToRoles[a]) agentToRoles[a] = [];
      agentToRoles[a].push(rc.label);
    }
  }

  if (loading) return <div className="text-center py-12 text-navy-400 dark:text-navy-300 text-sm">Loading agent configurations...</div>;

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-sm font-black text-navy-900 dark:text-cream-50 uppercase tracking-widest mb-2">Agent Provider & Model Configuration</h3>
        <p className="text-[10px] text-navy-400 dark:text-navy-300">Configure which AI provider and model each agent uses. Changes take effect on the next build or retry.</p>
      </div>

      <div className="bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded-xl p-5 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-[240px] flex-1">
            <h4 className="text-xs font-black text-navy-900 dark:text-cream-50 uppercase tracking-widest mb-2">Cost Profiles</h4>
            <p className="text-[10px] text-navy-400 dark:text-navy-300 mb-3">Apply a full OpenRouter-backed model mapping across every agent, then fine-tune individual agents below.</p>
            {defaultProfile && (
              <div className="inline-flex items-center gap-2 rounded-full bg-cream-100 dark:bg-navy-700 px-3 py-1 text-[9px] font-black uppercase tracking-widest text-navy-500 dark:text-cream-200">
                Backend default: {profiles[defaultProfile]?.label || defaultProfile}
              </div>
            )}
            <div className="mt-3 text-[9px] font-mono text-emerald-600 dark:text-emerald-300">
              Migration note: legacy OpenRouter claude-sonnet-4.6 rows auto-normalize to z-ai/glm-5.2, and the selected profile is now persisted by the backend.
            </div>
          </div>
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="block text-[9px] font-black text-navy-400 dark:text-navy-300 uppercase tracking-widest mb-1">Apply Profile</label>
              <select
                value={selectedProfile}
                onChange={e => setSelectedProfile(e.target.value as AgentCostProfileName)}
                className="min-w-[220px] bg-cream-50 dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg px-3 py-2 text-sm text-navy-900 dark:text-cream-50 focus:border-gold-500 outline-none"
              >
                {profileEntries.map(([key, profile]) => (
                  <option key={key} value={key}>{profile.label}</option>
                ))}
              </select>
            </div>
            <button
              onClick={handleApplyProfile}
              disabled={applyingProfile !== null}
              className="px-4 py-2.5 bg-gold-500 hover:bg-gold-400 text-navy-900 text-[10px] font-black uppercase tracking-widest rounded-lg transition disabled:opacity-50"
            >
              {applyingProfile === selectedProfile ? 'Applying...' : 'Apply Profile'}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          {profileEntries.map(([key, profile]) => (
            <button
              key={key}
              type="button"
              onClick={() => setSelectedProfile(key as AgentCostProfileName)}
              className={`text-left rounded-lg border p-4 transition ${selectedProfile === key ? 'border-gold-500 bg-gold-500/10' : 'border-cream-200 dark:border-navy-700 bg-cream-50 dark:bg-navy-900/60 hover:border-gold-500/40'}`}
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-[10px] font-black uppercase tracking-widest text-navy-900 dark:text-cream-50">{profile.label}</span>
                {defaultProfile === key && <span className="text-[8px] font-black uppercase tracking-widest text-emerald-500">default</span>}
              </div>
              <p className="text-[10px] text-navy-400 dark:text-navy-300">{profile.description}</p>
              <div className="mt-2 text-[9px] font-mono text-emerald-600 dark:text-emerald-300">Applied profiles become the persisted backend default and legacy claude-sonnet-4.6 entries migrate forward on read.</div>
            </button>
          ))}
        </div>
      </div>

      {message && (
        <div className={`rounded-lg p-3 mb-4 text-xs font-bold ${message.type === 'success' ? 'bg-green-600/10 border border-green-500/30 text-green-400' : 'bg-red-600/10 border border-red-500/30 text-red-400'}`}>
          {message.text}
        </div>
      )}

      <div className="space-y-3">
        {UNIQUE_AGENT_TYPES.map(agentType => {
          const edit = edits[agentType];
          if (!edit) return null;
          const changed = hasChanges(agentType);
          const isSaving = saving === agentType;
          const suggestions = MODEL_SUGGESTIONS[edit.provider] || [];
          const usedBy = agentToRoles[agentType] || [];

          return (
            <div key={agentType} className={`bg-white dark:bg-navy-800 border rounded-xl p-5 ${changed ? 'border-gold-500/50' : 'border-cream-300 dark:border-navy-600'}`}>
              <div className="flex items-start gap-4 flex-wrap">
                <div className="min-w-[140px]">
                  <span className="text-[9px] font-black uppercase tracking-wider bg-cream-200 dark:bg-navy-700 text-navy-600 dark:text-cream-200 px-2 py-1 rounded">
                    {ALL_AGENT_LABELS[agentType] || agentType}
                  </span>
                  <p className="text-[9px] text-navy-400 dark:text-navy-300 mt-1.5 font-mono">{agentType}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {usedBy.map(r => <span key={r} className="text-[7px] font-bold bg-cream-100 dark:bg-navy-900 text-navy-400 dark:text-navy-300 px-1 py-0.5 rounded">{r}</span>)}
                  </div>
                </div>
                <div className="flex-1 min-w-[160px]">
                  <label className="block text-[9px] font-black text-navy-400 dark:text-navy-300 uppercase tracking-widest mb-1">Provider</label>
                  <select value={edit.provider} onChange={e => updateEdit(agentType, 'provider', e.target.value)}
                    className="w-full bg-cream-50 dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg px-3 py-2 text-sm text-navy-900 dark:text-cream-50 focus:border-gold-500 outline-none">
                    {PROVIDER_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
                <div className="flex-[2] min-w-[220px]">
                  <label className="block text-[9px] font-black text-navy-400 dark:text-navy-300 uppercase tracking-widest mb-1">Model</label>
                  <input type="text" value={edit.model} onChange={e => updateEdit(agentType, 'model', e.target.value)} list={`models-${agentType}`}
                    className="w-full bg-cream-50 dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg px-3 py-2 text-sm text-navy-900 dark:text-cream-50 font-mono focus:border-gold-500 outline-none" placeholder="Model name" />
                  <datalist id={`models-${agentType}`}>{suggestions.map(m => <option key={m} value={m} />)}</datalist>
                </div>
                <div className="flex items-end">
                  <button onClick={() => handleSave(agentType)} disabled={!changed || isSaving}
                    className={`text-[9px] font-black uppercase tracking-wider px-4 py-2.5 rounded-lg transition ${changed ? 'bg-gold-500 hover:bg-gold-400 text-navy-900' : 'bg-cream-200 dark:bg-navy-700 text-navy-400 dark:text-navy-300 cursor-not-allowed'} disabled:opacity-50`}>
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// Freshness Operations Panel
// ============================================================

function FreshnessOpsPanel({ adminKey, onStatsRefresh }: { adminKey: string; onStatsRefresh: () => void }) {
  const [previewLimit, setPreviewLimit] = useState(25);
  const [preview, setPreview] = useState<FreshnessStatusResponse | null>(null);
  const [turnoverPreview, setTurnoverPreview] = useState<FreshnessTurnoverPreviewResponse | null>(null);
  const [lastRun, setLastRun] = useState<FreshnessRunResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [turnoverLoading, setTurnoverLoading] = useState(false);
  const [runningAction, setRunningAction] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [successorDrafts, setSuccessorDrafts] = useState<Record<string, string>>({});
  const [turnoverSuccessorDrafts, setTurnoverSuccessorDrafts] = useState<Record<string, string>>({});

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const result = await adminFetchFreshnessStatus(adminKey, previewLimit);
      setPreview(result);
    } catch (err: any) {
      setMessage(`Preview failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [adminKey, previewLimit]);

  const loadTurnoverPreview = useCallback(async () => {
    setTurnoverLoading(true);
    setMessage(null);
    try {
      const result = await adminFetchTurnoverPreview(adminKey, { limit: previewLimit });
      setTurnoverPreview(result);
    } catch (err: any) {
      setMessage(`Turnover preview failed: ${err.message}`);
    } finally {
      setTurnoverLoading(false);
    }
  }, [adminKey, previewLimit]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const runAction = async (
    action: string,
    request: {
      limit?: number;
      dryRun?: boolean;
      force?: boolean;
      refreshOfficials?: boolean;
      backfillArchiveSnapshots?: boolean;
      processPasses?: number;
    },
  ) => {
    setRunningAction(action);
    setMessage(null);
    try {
      const result = await adminRunFreshness(adminKey, request);
      setLastRun(result);
      await loadPreview();
      onStatsRefresh();
    } catch (err: any) {
      setMessage(`${action} failed: ${err.message}`);
    } finally {
      setRunningAction(null);
    }
  };

  const handleSuccessorDraftChange = (officialId: string, value: string) => {
    setSuccessorDrafts(prev => ({ ...prev, [officialId]: value }));
  };

  const handleTurnoverSuccessorDraftChange = (key: string, value: string) => {
    setTurnoverSuccessorDrafts(prev => ({ ...prev, [key]: value }));
  };

  const handleSuccessorLink = async (item: { officialId: string; officialName: string }, successorValue?: string | null) => {
    const draftValue = successorValue !== undefined ? successorValue : (successorDrafts[item.officialId] ?? '');
    const successorOfficialId = draftValue.trim() || null;
    setRunningAction(`successor:${item.officialId}`);
    setMessage(null);
    try {
      const result = await adminUpdateOfficialSuccessor(adminKey, item.officialId, { successorOfficialId });
      await Promise.all([loadPreview(), turnoverPreview ? loadTurnoverPreview() : Promise.resolve()]);
      setSuccessorDrafts(prev => ({ ...prev, [item.officialId]: result.successorOfficialId ?? '' }));
      setMessage(
        result.successorOfficialId
          ? `Linked successor for ${item.officialName}: ${result.replacedByOfficial?.name ?? result.successorOfficialId}`
          : `Cleared successor link for ${item.officialName}`,
      );
    } catch (err: any) {
      setMessage(`successor link failed: ${err.message}`);
    } finally {
      setRunningAction(null);
    }
  };

  const handleArchiveCandidate = async (item: BulkArchiveRequest) => {
    const request = item;
    if (!request) return;
    setRunningAction(`archive:${request.key}`);
    setMessage(null);
    try {
      const result = await adminArchiveOfficial(adminKey, request.officialId, {
        reason: request.reason,
        replaced_by_official_id: request.replaced_by_official_id,
      });
      await Promise.all([loadPreview(), loadTurnoverPreview()]);
      onStatsRefresh();
      setTurnoverSuccessorDrafts(prev => ({ ...prev, [request.key]: result.successorOfficialId ?? request.replaced_by_official_id ?? '' }));
      setMessage(
        result.successorOfficialId
          ? `Archived ${item.officialName} with successor ${result.replacedByOfficial?.name ?? result.successorOfficialId}`
          : `Archived ${item.officialName}`,
      );
    } catch (err: any) {
      setMessage(`archive failed for ${item.officialName}: ${err.message}`);
    } finally {
      setRunningAction(null);
    }
  };

  const handleArchiveAllCandidates = async () => {
    if (turnoverPreviewItems.length === 0) {
      setMessage('No loaded turnover candidates to archive.');
      return;
    }

    setRunningAction('archive_all_turnover');
    setMessage(null);

    let archived = 0;
    const failures: string[] = [];

    for (const request of turnoverPreviewItems) {
      try {
        const result = await adminArchiveOfficial(adminKey, request.officialId, {
          reason: request.reason,
          replaced_by_official_id: request.replaced_by_official_id,
        });
        archived += 1;
        setTurnoverSuccessorDrafts(prev => ({ ...prev, [request.key]: result.successorOfficialId ?? request.replaced_by_official_id ?? '' }));
      } catch (err: any) {
        failures.push(`${request.officialName}: ${err.message}`);
      }
    }

    await Promise.all([loadPreview(), loadTurnoverPreview()]);
    onStatsRefresh();
    setMessage(
      failures.length > 0
        ? `Archived ${archived}/${turnoverPreviewItems.length} turnover candidates. Failures: ${failures.slice(0, 3).join(' | ')}`
        : `Archived ${archived} turnover candidates from the loaded preview.`,
    );
    setRunningAction(null);
  };

  const previewCandidates = preview?.candidates ?? [];
  const previewCount = preview?.totalCandidates ?? 0;
  const archivedCount = preview?.archives.totalArchivedOfficials ?? 0;
  const successorReviewTotal = preview?.archives.successorReviewTotal ?? 0;
  const successorReviewItems = preview?.archives.successorReviewItems ?? [];
  const snapshotBackfillReviewTotal = preview?.archives.snapshotBackfillReviewTotal ?? 0;
  const snapshotBackfillReviewItems = preview?.archives.snapshotBackfillReviewItems ?? [];
  const turnoverCongress = turnoverPreview?.congress;
  const turnoverStates = turnoverPreview?.states;
  const turnoverPreviewItems = buildBulkArchiveRequests(
    [...(turnoverCongress?.items ?? []), ...(turnoverStates?.items ?? [])].slice(0, previewLimit),
    turnoverSuccessorDrafts,
  );
  const snapshotsCreated = lastRun?.ingestion.archiveBackfill?.snapshotsCreated ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-sm font-black text-navy-900 dark:text-cream-50 uppercase tracking-widest mb-1">Freshness + Archive Operations</h3>
          <p className="text-[10px] text-navy-400 dark:text-navy-300">Preview stale or incomplete profiles, queue dossier work, refresh officeholder rosters, and backfill frozen archive snapshots.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[9px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest">Preview Limit</label>
          <select
            value={previewLimit}
            onChange={e => setPreviewLimit(Number(e.target.value))}
            className="bg-cream-50 dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg px-3 py-2 text-xs text-navy-900 dark:text-cream-50"
          >
            {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <button
            onClick={loadPreview}
            disabled={loading || turnoverLoading || !!runningAction}
            className="px-4 py-2 bg-cream-200 dark:bg-navy-700 hover:bg-cream-300 dark:hover:bg-navy-600 text-navy-700 dark:text-cream-200 text-[10px] font-black uppercase tracking-widest rounded-lg transition disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Refresh Preview'}
          </button>
          <button
            onClick={loadTurnoverPreview}
            disabled={loading || turnoverLoading || !!runningAction}
            className="px-4 py-2 bg-purple-600 text-white text-[10px] font-black uppercase tracking-widest rounded-lg hover:bg-purple-700 disabled:opacity-50 transition"
          >
            {turnoverLoading ? 'Loading...' : 'Turnover Preview'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="bg-cream-100 dark:bg-navy-700 rounded-lg p-4">
          <div className="text-2xl font-black text-navy-900 dark:text-cream-50">{previewCount}</div>
          <div className="text-[9px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest mt-1">Profiles Needing Work</div>
        </div>
        <div className="bg-cream-100 dark:bg-navy-700 rounded-lg p-4">
          <div className="text-2xl font-black text-amber-600 dark:text-amber-400">{previewCandidates.filter(c => c.missingSections.length > 0).length}</div>
          <div className="text-[9px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest mt-1">Missing Dossiers</div>
        </div>
        <div className="bg-cream-100 dark:bg-navy-700 rounded-lg p-4">
          <div className="text-2xl font-black text-blue-600 dark:text-blue-400">{archivedCount}</div>
          <div className="text-[9px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest mt-1">Archived Profiles</div>
        </div>
        <div className="bg-cream-100 dark:bg-navy-700 rounded-lg p-4">
          <div className="text-2xl font-black text-purple-600 dark:text-purple-400">{successorReviewTotal}</div>
          <div className="text-[9px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest mt-1">Successor Review Queue</div>
        </div>
        <div className="bg-cream-100 dark:bg-navy-700 rounded-lg p-4">
          <div className="text-2xl font-black text-orange-600 dark:text-orange-400">{snapshotBackfillReviewTotal}</div>
          <div className="text-[9px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest mt-1">Snapshot Backfill Queue</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => runAction('queue_missing', { limit: previewLimit, refreshOfficials: false, backfillArchiveSnapshots: false, processPasses: 2 })}
          disabled={!!runningAction}
          className="px-4 py-2 bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
        >
          {runningAction === 'queue_missing' ? 'Queuing...' : 'Queue Missing/Expired'}
        </button>
        <button
          onClick={() => runAction('refresh_rosters', { limit: previewLimit, refreshOfficials: true, backfillArchiveSnapshots: true, processPasses: 2 })}
          disabled={!!runningAction}
          className="px-4 py-2 bg-emerald-600 text-white text-[10px] font-black uppercase tracking-widest rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition"
        >
          {runningAction === 'refresh_rosters' ? 'Refreshing...' : 'Refresh Rosters + Queue'}
        </button>
        <button
          onClick={() => runAction('backfill_archives', { limit: previewLimit, refreshOfficials: false, backfillArchiveSnapshots: true, processPasses: 0 })}
          disabled={!!runningAction}
          className="px-4 py-2 bg-orange-600 text-white text-[10px] font-black uppercase tracking-widest rounded-lg hover:bg-orange-700 disabled:opacity-50 transition"
        >
          {runningAction === 'backfill_archives' ? 'Backfilling...' : 'Backfill Archive Snapshots'}
        </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-cream-300 dark:border-navy-600 flex items-center justify-between">
            <div>
              <h4 className="text-xs font-black text-navy-900 dark:text-cream-50 uppercase tracking-widest">Successor Review Queue</h4>
              <p className="text-[10px] text-navy-400 dark:text-navy-300">Archived officials still missing a successor link.</p>
            </div>
            <span className="text-[10px] font-bold text-purple-600 dark:text-purple-400">{successorReviewTotal} total</span>
          </div>
          <div className="divide-y divide-cream-200 dark:divide-navy-700/50">
            {successorReviewItems.map((item) => {
              const reviewSummary = buildSuccessorReviewSummary(item);
              return (
              <div key={item.officialId} className="px-5 py-4 flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-sm font-bold text-navy-900 dark:text-cream-50">{item.officialName}</div>
                  <div className="text-[10px] text-navy-400 dark:text-navy-300">{[item.officeName, item.state, item.district ? `District ${item.district}` : null].filter(Boolean).join(' · ')}</div>
                  <div className="mt-2 text-[10px] text-navy-500 dark:text-navy-300">{reviewSummary.detailLabel}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <div className="text-[9px] font-black uppercase tracking-widest bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 px-2 py-1 rounded">
                    {reviewSummary.badgeLabel}
                  </div>
                  <input
                    type="text"
                    value={successorDrafts[item.officialId] ?? reviewSummary.defaultDraftValue}
                    onChange={e => handleSuccessorDraftChange(item.officialId, e.target.value)}
                    placeholder="Successor official ID"
                    className="min-w-[180px] bg-cream-50 dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded px-3 py-1.5 text-[10px] text-navy-900 dark:text-cream-50"
                  />
                  <button
                    onClick={() => handleSuccessorLink(item)}
                    disabled={runningAction === `successor:${item.officialId}`}
                    className="px-3 py-1.5 bg-purple-600 text-white text-[9px] font-black uppercase tracking-widest rounded hover:bg-purple-700 disabled:opacity-50 transition"
                  >
                    {runningAction === `successor:${item.officialId}` ? 'Saving...' : 'Save Successor'}
                  </button>
                  <button
                    onClick={() => handleSuccessorLink(item, null)}
                    disabled={runningAction === `successor:${item.officialId}`}
                    className="px-3 py-1.5 bg-cream-200 dark:bg-navy-700 text-navy-700 dark:text-cream-200 text-[9px] font-black uppercase tracking-widest rounded hover:bg-cream-300 dark:hover:bg-navy-600 disabled:opacity-50 transition"
                  >
                    Clear
                  </button>
                </div>
              </div>
            );})}
            {successorReviewItems.length === 0 && (
              <div className="px-5 py-8 text-center text-sm text-navy-400 dark:text-navy-300">
                No archived officials currently need successor review.
              </div>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-cream-300 dark:border-navy-600 flex items-center justify-between">
            <div>
              <h4 className="text-xs font-black text-navy-900 dark:text-cream-50 uppercase tracking-widest">Turnover Preview</h4>
              <p className="text-[10px] text-navy-400 dark:text-navy-300">No-write archive preview for upcoming roster changes.</p>
            </div>
            <span className="text-[10px] font-bold text-navy-400 dark:text-navy-300">{turnoverPreview ? 'Loaded' : 'Not loaded'}</span>
          </div>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-cream-100 dark:bg-navy-700 rounded-lg p-4">
                <div className="text-lg font-black text-navy-900 dark:text-cream-50">{turnoverCongress?.total ?? 0}</div>
                <div className="text-[9px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest mt-1">Congress Archive Candidates</div>
                {turnoverCongress?.skipped && <div className="mt-2 text-[10px] text-navy-400 dark:text-navy-300">{turnoverCongress.skipped}</div>}
              </div>
              <div className="bg-cream-100 dark:bg-navy-700 rounded-lg p-4">
                <div className="text-lg font-black text-navy-900 dark:text-cream-50">{turnoverStates?.total ?? 0}</div>
                <div className="text-[9px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest mt-1">State Archive Candidates</div>
                {turnoverStates?.skipped && <div className="mt-2 text-[10px] text-navy-400 dark:text-navy-300">{turnoverStates.skipped}</div>}
              </div>
            </div>
            {turnoverPreview && (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3 flex-wrap rounded-lg bg-cream-50 dark:bg-navy-900 px-4 py-3 border border-cream-200 dark:border-navy-700/50">
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-navy-500 dark:text-navy-300">Bulk archive</div>
                    <div className="text-[10px] text-navy-400 dark:text-navy-300">Archive every candidate currently loaded in this turnover preview window.</div>
                  </div>
                  <button
                    onClick={handleArchiveAllCandidates}
                    disabled={runningAction === 'archive_all_turnover' || turnoverPreviewItems.length === 0}
                    className="px-4 py-2 bg-orange-700 text-white text-[10px] font-black uppercase tracking-widest rounded-lg hover:bg-orange-800 disabled:opacity-50 transition"
                  >
                    {runningAction === 'archive_all_turnover' ? 'Archiving All...' : `Archive All Loaded (${turnoverPreviewItems.length})`}
                  </button>
                </div>
                {turnoverPreviewItems.map((item) => {
                  const draftKey = item.key;
                  const archiveActionKey = `archive:${draftKey}`;
                  return (
                    <div key={item.key} className="rounded-lg bg-cream-50 dark:bg-navy-900 px-4 py-3 flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <div className="text-sm font-bold text-navy-900 dark:text-cream-50">{item.officialName}</div>
                        <div className="text-[10px] text-navy-400 dark:text-navy-300">{[item.officeName, item.state, item.district ? `District ${item.district}` : null].filter(Boolean).join(' · ')}</div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <div className="flex flex-wrap gap-2 justify-end">
                          <span className="text-[9px] font-black uppercase tracking-widest bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-2 py-1 rounded">{item.reason === 'replaced_after_election' ? 'Seat replaced' : 'Missing from roster'}</span>
                          <span className="text-[9px] font-black uppercase tracking-widest bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-1 rounded">{item.replaced_by_official_id ? `Successor ID: ${item.replaced_by_official_id}` : 'No successor matched'}</span>
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          <input
                            type="text"
                            value={turnoverSuccessorDrafts[draftKey] ?? item.replaced_by_official_id ?? ''}
                            onChange={e => handleTurnoverSuccessorDraftChange(draftKey, e.target.value)}
                            placeholder="Successor official ID (optional)"
                            className="min-w-[220px] bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded px-3 py-1.5 text-[10px] text-navy-900 dark:text-cream-50"
                          />
                          <button
                            onClick={() => handleArchiveCandidate(item)}
                            disabled={runningAction === archiveActionKey}
                            className="px-3 py-1.5 bg-orange-600 text-white text-[9px] font-black uppercase tracking-widest rounded hover:bg-orange-700 disabled:opacity-50 transition"
                          >
                            {runningAction === archiveActionKey ? 'Archiving...' : 'Archive Candidate'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-cream-300 dark:border-navy-600 flex items-center justify-between">
          <div>
            <h4 className="text-xs font-black text-navy-900 dark:text-cream-50 uppercase tracking-widest">Snapshot Backfill Review Queue</h4>
            <p className="text-[10px] text-navy-400 dark:text-navy-300">Archived officials missing a frozen archive snapshot before long-term retention.</p>
          </div>
          <span className="text-[10px] font-bold text-orange-600 dark:text-orange-400">{snapshotBackfillReviewTotal} total</span>
        </div>
        <div className="divide-y divide-cream-200 dark:divide-navy-700/50">
          {snapshotBackfillReviewItems.map((item) => {
            const snapshotSummary = buildSnapshotReviewSummary(item);
            return (
            <div key={item.officialId} className="px-5 py-4 flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-sm font-bold text-navy-900 dark:text-cream-50">{item.officialName}</div>
                <div className="text-[10px] text-navy-400 dark:text-navy-300">{[item.officeName, item.state, item.district ? `District ${item.district}` : null].filter(Boolean).join(' · ')}</div>
                <div className="mt-2 text-[10px] text-navy-500 dark:text-navy-300">{snapshotSummary.detailLabel}</div>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <div className="text-[9px] font-black uppercase tracking-widest bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 px-2 py-1 rounded">
                  {snapshotSummary.badgeLabel}
                </div>
                {item.archiveReason && (
                  <div className="text-[9px] font-black uppercase tracking-widest bg-cream-200 dark:bg-navy-700 text-navy-600 dark:text-cream-200 px-2 py-1 rounded">
                    {item.archiveReason}
                  </div>
                )}
              </div>
            </div>
          );})}
          {snapshotBackfillReviewItems.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-navy-400 dark:text-navy-300">
              No archived officials currently need snapshot backfill review.
            </div>
          )}
        </div>
      </div>

      {message && (
        <div className="rounded-lg border border-red-500/30 bg-red-600/10 px-4 py-3 text-xs font-bold text-red-400">
          {message}
        </div>
      )}

      {lastRun && (
        <div className="bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h4 className="text-xs font-black text-navy-900 dark:text-cream-50 uppercase tracking-widest">Last Operation</h4>
              <p className="text-[10px] text-navy-400 dark:text-navy-300">{lastRun.status} · {lastRun.totalCandidates} candidates · {lastRun.totalEnqueued} tasks enqueued</p>
            </div>
            <div className="text-[10px] font-bold text-navy-400 dark:text-navy-300">
              Backfilled snapshots: <span className="text-orange-500">{snapshotsCreated}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-cream-100 dark:bg-navy-700 rounded-lg p-3 text-center">
              <div className="text-lg font-black text-navy-700 dark:text-cream-200">{lastRun.ingestion.congress?.updated ?? 0}</div>
              <div className="text-[9px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest">Congress Updated</div>
            </div>
            <div className="bg-cream-100 dark:bg-navy-700 rounded-lg p-3 text-center">
              <div className="text-lg font-black text-navy-700 dark:text-cream-200">{lastRun.ingestion.states?.updated ?? 0}</div>
              <div className="text-[9px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest">States Updated</div>
            </div>
            <div className="bg-cream-100 dark:bg-navy-700 rounded-lg p-3 text-center">
              <div className="text-lg font-black text-emerald-600 dark:text-emerald-400">{lastRun.queueRuns.reduce((sum, run) => sum + (run.completed ?? 0), 0)}</div>
              <div className="text-[9px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest">Queue Completed</div>
            </div>
            <div className="bg-cream-100 dark:bg-navy-700 rounded-lg p-3 text-center">
              <div className="text-lg font-black text-red-600 dark:text-red-400">{lastRun.queueRuns.reduce((sum, run) => sum + (run.failed ?? 0), 0)}</div>
              <div className="text-[9px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest">Queue Failed</div>
            </div>
          </div>

          {lastRun.notes.length > 0 && (
            <div>
              <h5 className="text-[10px] font-black text-navy-500 dark:text-navy-300 uppercase tracking-widest mb-2">Notes</h5>
              <ul className="space-y-1 text-[10px] text-navy-500 dark:text-navy-300">
                {lastRun.notes.map((note, index) => <li key={`${index}-${note}`}>• {note}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-cream-300 dark:border-navy-600 flex items-center justify-between">
          <h4 className="text-xs font-black text-navy-900 dark:text-cream-50 uppercase tracking-widest">Preview Candidates</h4>
          <span className="text-[10px] font-bold text-navy-400 dark:text-navy-300">{previewCount} shown</span>
        </div>
        <div className="divide-y divide-cream-200 dark:divide-navy-700/50">
          {previewCandidates.map((candidate) => (
            <div key={candidate.officialId} className="px-5 py-4 flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-sm font-bold text-navy-900 dark:text-cream-50">{candidate.name}</div>
                <div className="text-[10px] font-bold text-navy-400 dark:text-navy-300 uppercase tracking-widest">{personTypeLabel(candidate.personType)}</div>
              </div>
              <div className="flex flex-wrap gap-2 max-w-2xl justify-end">
                {candidate.missingPhoto && <span className="text-[9px] font-black uppercase tracking-widest bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 px-2 py-1 rounded">Photo Missing</span>}
                {candidate.missingSections.map((section) => (
                  <span key={`missing-${candidate.officialId}-${section}`} className="text-[9px] font-black uppercase tracking-widest bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-2 py-1 rounded">
                    Missing {section}
                  </span>
                ))}
                {candidate.staleSections.map((section) => (
                  <span key={`stale-${candidate.officialId}-${section}`} className="text-[9px] font-black uppercase tracking-widest bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-1 rounded">
                    Stale {section}
                  </span>
                ))}
              </div>
            </div>
          ))}
          {previewCandidates.length === 0 && (
            <div className="px-5 py-10 text-center text-sm text-navy-400 dark:text-navy-300">
              No current freshness candidates in this preview window.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// System Panel
// ============================================================

function SystemPanel({ adminKey, onStatsRefresh }: { adminKey: string; onStatsRefresh: () => void }) {
  const [migrationResult, setMigrationResult] = useState<string | null>(null);
  const [purgeResult, setPurgeResult] = useState<string | null>(null);
  const [running, setRunning] = useState<string | null>(null);
  const [newsletterSummary, setNewsletterSummary] = useState<NewsletterSummary | null>(null);
  const [newsletterIssues, setNewsletterIssues] = useState<NewsletterIssue[]>([]);
  const [newsletterSubscribers, setNewsletterSubscribers] = useState<NewsletterSubscriber[]>([]);
  const [newsletterPreview, setNewsletterPreview] = useState<NewsletterIssue | null>(null);
  const [newsletterLog, setNewsletterLog] = useState<string | null>(null);
  const [testEmail, setTestEmail] = useState('');

  const loadNewsletter = useCallback(async () => {
    const [summary, issuesResult, subscribersResult] = await Promise.all([
      adminFetchNewsletterSummary(adminKey),
      adminFetchNewsletterIssues(adminKey, 6),
      adminFetchNewsletterSubscribers(adminKey, 'active', 12),
    ]);
    setNewsletterSummary(summary);
    setNewsletterIssues(issuesResult.issues);
    setNewsletterSubscribers(subscribersResult.subscribers);
    if (!newsletterPreview && issuesResult.issues.length > 0) {
      setNewsletterPreview(issuesResult.issues[0]);
    }
  }, [adminKey, newsletterPreview]);

  useEffect(() => {
    loadNewsletter().catch((err: any) => setNewsletterLog(`Error: ${err.message}`));
  }, [loadNewsletter]);

  const runMigration = async () => {
    setRunning('migrate');
    try { const result = await adminRunMigration(adminKey); setMigrationResult(JSON.stringify(result, null, 2)); }
    catch (err: any) { setMigrationResult(`Error: ${err.message}`); }
    setRunning(null);
  };

  const runPurge = async () => {
    setRunning('purge');
    try { const result = await adminPurgeExpired(adminKey); setPurgeResult(`Purged ${result.rowsAffected} expired cache entries`); onStatsRefresh(); }
    catch (err: any) { setPurgeResult(`Error: ${err.message}`); }
    setRunning(null);
  };

  const generatePreview = async (refreshBeforeGenerate = false) => {
    setRunning(refreshBeforeGenerate ? 'newsletter-refresh-preview' : 'newsletter-preview');
    try {
      const result = await adminGenerateNewsletter(adminKey, { refreshBeforeGenerate, lookbackDays: 7, processPasses: 2 });
      setNewsletterPreview(result.issue);
      setNewsletterLog(JSON.stringify(result, null, 2));
      await loadNewsletter();
      onStatsRefresh();
    } catch (err: any) {
      setNewsletterLog(`Error: ${err.message}`);
    }
    setRunning(null);
  };

  const sendTest = async () => {
    if (!newsletterPreview?.id) {
      setNewsletterLog('Generate a preview issue before sending a test email.');
      return;
    }
    setRunning('newsletter-test');
    try {
      const result = await adminSendNewsletterTest(adminKey, newsletterPreview.id, testEmail || undefined);
      setNewsletterLog(JSON.stringify(result, null, 2));
      await loadNewsletter();
    } catch (err: any) {
      setNewsletterLog(`Error: ${err.message}`);
    }
    setRunning(null);
  };

  const runWeekly = async () => {
    setRunning('newsletter-weekly');
    try {
      const result = await adminRunWeeklyNewsletter(adminKey);
      setNewsletterPreview(result.issue);
      setNewsletterLog(JSON.stringify(result, null, 2));
      await loadNewsletter();
      onStatsRefresh();
    } catch (err: any) {
      setNewsletterLog(`Error: ${err.message}`);
    }
    setRunning(null);
  };

  return (
    <div className="space-y-8">
      <FreshnessOpsPanel adminKey={adminKey} onStatsRefresh={onStatsRefresh} />

      <div>
        <h3 className="text-sm font-black text-navy-900 dark:text-cream-50 uppercase tracking-widest mb-4">Weekly Newsletter</h3>
        <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded-xl p-6 space-y-4">
            <div className="grid sm:grid-cols-5 gap-3">
              <div className="rounded-lg bg-cream-50 dark:bg-navy-900 p-3">
                <div className="text-[10px] uppercase tracking-widest text-navy-400 dark:text-navy-300">Active Subs</div>
                <div className="text-xl font-black text-navy-900 dark:text-cream-50">{newsletterSummary?.counts.activeSubscribers ?? 0}</div>
              </div>
              <div className="rounded-lg bg-cream-50 dark:bg-navy-900 p-3">
                <div className="text-[10px] uppercase tracking-widest text-navy-400 dark:text-navy-300">Total Subs</div>
                <div className="text-xl font-black text-navy-900 dark:text-cream-50">{newsletterSummary?.counts.totalSubscribers ?? 0}</div>
              </div>
              <div className="rounded-lg bg-cream-50 dark:bg-navy-900 p-3">
                <div className="text-[10px] uppercase tracking-widest text-navy-400 dark:text-navy-300">Issues</div>
                <div className="text-xl font-black text-navy-900 dark:text-cream-50">{newsletterSummary?.counts.totalIssues ?? 0}</div>
              </div>
              <div className="rounded-lg bg-cream-50 dark:bg-navy-900 p-3">
                <div className="text-[10px] uppercase tracking-widest text-navy-400 dark:text-navy-300">Sent</div>
                <div className="text-xl font-black text-navy-900 dark:text-cream-50">{newsletterSummary?.counts.sentDeliveries ?? 0}</div>
              </div>
              <div className="rounded-lg bg-cream-50 dark:bg-navy-900 p-3">
                <div className="text-[10px] uppercase tracking-widest text-navy-400 dark:text-navy-300">Failed</div>
                <div className="text-xl font-black text-navy-900 dark:text-cream-50">{newsletterSummary?.counts.failedDeliveries ?? 0}</div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button onClick={() => generatePreview(false)} disabled={!!running}
                className="text-[10px] font-black uppercase tracking-widest bg-blue-600/20 hover:bg-blue-600 text-blue-500 hover:text-white px-4 py-2 rounded transition disabled:opacity-50">
                {running === 'newsletter-preview' ? 'Generating...' : 'Generate Preview'}
              </button>
              <button onClick={() => generatePreview(true)} disabled={!!running}
                className="text-[10px] font-black uppercase tracking-widest bg-indigo-600/20 hover:bg-indigo-600 text-indigo-500 hover:text-white px-4 py-2 rounded transition disabled:opacity-50">
                {running === 'newsletter-refresh-preview' ? 'Refreshing...' : 'Refresh + Generate'}
              </button>
              <button onClick={runWeekly} disabled={!!running}
                className="text-[10px] font-black uppercase tracking-widest bg-gold-500/20 hover:bg-gold-500 text-gold-600 hover:text-navy-950 px-4 py-2 rounded transition disabled:opacity-50">
                {running === 'newsletter-weekly' ? 'Running...' : 'Run Weekly Send'}
              </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
                className="flex-1 rounded-lg border border-cream-300 dark:border-navy-600 bg-cream-50 dark:bg-navy-900 px-3 py-2 text-sm text-navy-900 dark:text-cream-50"
              />
              <button onClick={sendTest} disabled={!!running || !newsletterPreview}
                className="text-[10px] font-black uppercase tracking-widest bg-green-600/20 hover:bg-green-600 text-green-500 hover:text-white px-4 py-2 rounded transition disabled:opacity-50">
                {running === 'newsletter-test' ? 'Sending...' : 'Send Test Email'}
              </button>
            </div>

            {newsletterPreview && (
              <div className="rounded-xl border border-cream-300 dark:border-navy-600 overflow-hidden">
                <div className="px-4 py-3 bg-cream-50 dark:bg-navy-900 border-b border-cream-300 dark:border-navy-600">
                  <div className="text-xs font-black text-navy-900 dark:text-cream-50 uppercase tracking-widest">{newsletterPreview.subject}</div>
                  <div className="text-[10px] text-navy-400 dark:text-navy-300 mt-1">{newsletterPreview.slug} · {newsletterPreview.status}</div>
                </div>
                <div className="max-h-[28rem] overflow-auto bg-white dark:bg-navy-950 p-4">
                  <div dangerouslySetInnerHTML={{ __html: newsletterPreview.htmlContent }} />
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded-xl p-6">
              <h4 className="text-xs font-bold text-navy-600 dark:text-cream-200 mb-3">Recent Issues</h4>
              <div className="space-y-2">
                {newsletterIssues.map((issue) => (
                  <button
                    key={issue.id}
                    onClick={() => setNewsletterPreview(issue)}
                    className="w-full text-left rounded-lg border border-cream-200 dark:border-navy-700 px-3 py-3 hover:border-gold-500 transition"
                  >
                    <div className="text-[10px] font-black uppercase tracking-widest text-navy-400 dark:text-navy-300">{issue.status}</div>
                    <div className="text-sm font-bold text-navy-900 dark:text-cream-50 mt-1">{issue.subject}</div>
                  </button>
                ))}
                {newsletterIssues.length === 0 && <div className="text-sm text-navy-400 dark:text-navy-300">No issues generated yet.</div>}
              </div>
            </div>

            <div className="bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded-xl p-6">
              <h4 className="text-xs font-bold text-navy-600 dark:text-cream-200 mb-3">Active Subscribers</h4>
              <div className="space-y-2 max-h-48 overflow-auto">
                {newsletterSubscribers.map((subscriber) => (
                  <div key={subscriber.id} className="text-sm text-navy-900 dark:text-cream-50 border-b border-cream-200 dark:border-navy-700 pb-2 last:border-b-0">
                    {subscriber.email}
                  </div>
                ))}
                {newsletterSubscribers.length === 0 && <div className="text-sm text-navy-400 dark:text-navy-300">No active subscribers yet.</div>}
              </div>
            </div>

            {newsletterLog && (
              <div className="bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded-xl p-6">
                <h4 className="text-xs font-bold text-navy-600 dark:text-cream-200 mb-3">Newsletter Run Log</h4>
                <pre className="text-[10px] text-navy-500 dark:text-navy-300 bg-cream-50 dark:bg-navy-900 rounded p-3 overflow-x-auto max-h-72">{newsletterLog}</pre>
              </div>
            )}
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-black text-navy-900 dark:text-cream-50 uppercase tracking-widest mb-4">Database Operations</h3>
        <div className="flex flex-wrap gap-4">
          <div className="bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded-xl p-6 flex-1 min-w-[280px]">
            <h4 className="text-xs font-bold text-navy-600 dark:text-cream-200 mb-2">Run Migration</h4>
            <p className="text-[10px] text-navy-400 dark:text-navy-300 mb-4">Applies schema changes. Safe to run multiple times (idempotent).</p>
            <button onClick={runMigration} disabled={running === 'migrate'}
              className="text-[10px] font-black uppercase tracking-widest bg-blue-600/20 hover:bg-blue-600 text-blue-500 hover:text-white px-4 py-2 rounded transition disabled:opacity-50">
              {running === 'migrate' ? 'Running...' : 'Run Migration'}
            </button>
            {migrationResult && <pre className="mt-4 text-[10px] text-navy-500 dark:text-navy-300 bg-cream-50 dark:bg-navy-900 rounded p-3 overflow-x-auto max-h-40">{migrationResult}</pre>}
          </div>
          <div className="bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded-xl p-6 flex-1 min-w-[280px]">
            <h4 className="text-xs font-bold text-navy-600 dark:text-cream-200 mb-2">Purge Expired Cache</h4>
            <p className="text-[10px] text-navy-400 dark:text-navy-300 mb-4">Remove expired intelligence cache entries to free up space.</p>
            <button onClick={runPurge} disabled={running === 'purge'}
              className="text-[10px] font-black uppercase tracking-widest bg-orange-600/20 hover:bg-orange-600 text-orange-500 hover:text-white px-4 py-2 rounded transition disabled:opacity-50">
              {running === 'purge' ? 'Purging...' : 'Purge Expired'}
            </button>
            {purgeResult && <p className="mt-4 text-[10px] text-navy-500 dark:text-navy-300">{purgeResult}</p>}
          </div>
        </div>
      </div>
      <div>
        <h3 className="text-sm font-black text-navy-900 dark:text-cream-50 uppercase tracking-widest mb-4">Configuration</h3>
        <div className="bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded-xl p-6">
          <p className="text-[10px] text-navy-400 dark:text-navy-300 mb-3">Cloudflare config is split: Pages keeps core app secrets plus the newsletter service binding, while the standalone newsletter Worker owns email and cron configuration.</p>
          <div className="space-y-2">
            {['Pages: TURSO_DATABASE_URL', 'Pages: TURSO_AUTH_TOKEN', 'Pages: ADMIN_API_KEY', 'Pages: OPENROUTER_API_KEY', 'Pages: ANTHROPIC_API_KEY', 'Pages: OPENAI_API_KEY', 'Pages: XAI_API_KEY', 'Pages: CONGRESS_API_KEY', 'Pages: OPENSTATES_API_KEY', 'Pages: BLUESKY_IDENTIFIER', 'Pages: BLUESKY_APP_PASSWORD', 'Pages: NEWSLETTER service binding', 'Worker: EMAIL binding', 'Worker: NEWSLETTER_FROM_EMAIL', 'Worker: NEWSLETTER_REPLY_TO_EMAIL', 'Worker: NEWSLETTER_PUBLIC_BASE_URL', 'Worker: NEWSLETTER_TEST_RECIPIENT'].map(key => (
              <div key={key} className="flex items-center gap-3">
                <span className="text-[10px] font-bold text-navy-700 dark:text-cream-200 font-mono w-48">{key}</span>
                <span className="text-[9px] text-navy-400 dark:text-navy-300">Configured in Cloudflare dashboard</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Admin Dashboard — Main Page
// ============================================================

type TopTab = 'dossiers' | 'tasks' | 'queue' | 'reviews' | 'agents' | 'system';

export default function AdminPage() {
  const [adminKey, setAdminKey] = useState<string | null>(() => sessionStorage.getItem('aac_admin_key'));
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [activeTab, setActiveTab] = useState<TopTab>('dossiers');
  const [activeRole, setActiveRole] = useState(0); // index into ROLE_CONFIGS

  const loadStats = useCallback(async () => {
    if (!adminKey) return;
    try { const s = await adminFetchStats(adminKey); setStats(s); }
    catch { sessionStorage.removeItem('aac_admin_key'); setAdminKey(null); }
  }, [adminKey]);

  useEffect(() => { loadStats(); }, [loadStats]);

  const handleLogout = () => { sessionStorage.removeItem('aac_admin_key'); setAdminKey(null); };

  if (!adminKey) return <AdminAuth onAuth={setAdminKey} />;

  const TOP_TABS: { key: TopTab; label: string }[] = [
    { key: 'dossiers', label: 'Dossiers' },
    { key: 'tasks', label: 'Tasks' },
    { key: 'queue', label: 'Queue' },
    { key: 'reviews', label: 'Reviews' },
    { key: 'agents', label: 'Agents' },
    { key: 'system', label: 'System' },
  ];

  return (
    <div className="min-h-screen pb-12">
      {/* Header */}
      <header className="bg-white dark:bg-navy-800 border-b border-cream-300 dark:border-navy-600">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="text-navy-900 dark:text-cream-50 font-black text-xl uppercase tracking-tight hover:text-gold-500 transition">AAC</a>
            <div className="h-6 w-px bg-cream-300 dark:bg-navy-600" />
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-[10px] font-black text-navy-400 dark:text-navy-300 uppercase tracking-widest">Admin Control Panel</span>
            </div>
          </div>
          <button onClick={handleLogout} className="text-[10px] font-black uppercase tracking-widest text-navy-400 dark:text-navy-300 hover:text-red-500 transition">Logout</button>
        </div>
      </header>

      <div className="container mx-auto px-4 mt-8 space-y-6">
        {/* Stats */}
        {stats && <StatsBar stats={stats} />}

        {/* Top Tabs */}
        <div className="flex gap-1 bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded-lg p-1 w-fit">
          {TOP_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-5 py-2 rounded-md text-[10px] font-black uppercase tracking-widest transition ${
                activeTab === tab.key
                  ? 'bg-navy-800 dark:bg-gold-500 text-white dark:text-navy-900'
                  : 'text-navy-400 dark:text-navy-300 hover:text-navy-900 dark:hover:text-cream-50 hover:bg-cream-200 dark:hover:bg-navy-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Role sub-tabs (only when Dossiers tab is active) */}
        {activeTab === 'dossiers' && (
          <div className="flex gap-1 bg-cream-100 dark:bg-navy-900 border border-cream-200 dark:border-navy-700 rounded-lg p-1 w-fit">
            {ROLE_CONFIGS.map((rc, i) => (
              <button
                key={rc.personType}
                onClick={() => setActiveRole(i)}
                className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-[10px] font-black uppercase tracking-widest transition ${
                  activeRole === i
                    ? 'bg-white dark:bg-navy-800 text-navy-900 dark:text-cream-50 shadow-sm'
                    : 'text-navy-400 dark:text-navy-300 hover:text-navy-900 dark:hover:text-cream-50'
                }`}
              >
                <div className={`w-2 h-2 rounded-full ${rc.color}`} />
                {rc.label}
              </button>
            ))}
          </div>
        )}

        {/* Tab Content */}
        <div className="bg-white/50 dark:bg-navy-800/50 border border-cream-300 dark:border-navy-600 rounded-2xl p-6">
          {activeTab === 'dossiers' && <RoleManager adminKey={adminKey} config={ROLE_CONFIGS[activeRole]} />}
          {activeTab === 'tasks' && <TaskQueue adminKey={adminKey} />}
          {activeTab === 'queue' && <QueuePanel adminKey={adminKey} />}
          {activeTab === 'reviews' && <ContributorReviewPanel adminKey={adminKey} />}
          {activeTab === 'agents' && <AgentConfigPanel adminKey={adminKey} />}
          {activeTab === 'system' && <SystemPanel adminKey={adminKey} onStatsRefresh={loadStats} />}
        </div>
      </div>
    </div>
  );
}
